import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  ControlButton,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  NodeTypes,
  EdgeTypes,
  // addEdge,
  // ConnectionMode,  
  // MarkerType,
  // SelectionMode,
  // ReactFlowProvider,
  // NodeDragHandler,
  // OnSelectionChangeParams
} from "reactflow";
import "reactflow/dist/style.css";
// Removed Ant Design message - using console.log/error instead
import { BorderOuterOutlined } from "@ant-design/icons";
import { 
  DecisionVariablesRegistry, 
  DecisionVariable,
  GlobalVariableRegistry,
  VariablePlaceholder, 
  InterjectionGroup,
  ToolCall,
  FlowData
} from "../utils/types";
import {
  loadSavedData,
  extractPlaceholders,
  getNodeChildren,
  generateSessionId,
  NODE_WIDTH,
  getNextNodeId,
  resetNodeIdCounter,
  exportFlowData,
  importFlowData,
  convertFlowDataToConversationBuilderRequest,
  syncNodeIdCounterWithExistingNodes,
  generateUniqueIdentifier,
  clearAutoSavedData,
  getBlankInitialState
} from "../utils/utils";
import { ConversationBuilderService, ConversationBuilderSaveResponse } from "../../../../services/ConversationBuilderService";
import { convertBackendConfigToFlowData } from "../utils/backendConversion";
import { usePermissions } from "../../../../PermissionsProvider";
import CustomNode from "./CustomNode";
import CustomEdge from "./CustomEdge";
import Toolbar from "./Toolbar";
import ContextMenu from "./ContextMenu";
import CanvasContextMenu from "./CanvasContextMenu";
import Modals from "./Modals";
import ImportFlowModal from "./ImportFlowModal";
import ConfigSidebar from "./ConfigSidebar";
import LoadTreeModal from "./LoadTreeModal";
import SaveTreeModal from "./SaveTreeModal";
import { useCopyPasteOperations } from "../operations/copyPasteOperations";
import { useNodeOperations } from "../operations/nodeOperations";
import { useModalStates, useTreeState, useRefsAndInstance, useActionHistory, useLayoutOperations, useConfigSidebar, useManualSave, useDuplicateIdentifierDetection, DeleteModalState, ModalState, PlaceholderModalState, PreviewModalState, StandaloneModalState } from "../hooks/hooks";
import { validateFlow } from "../utils/validation";
import ValidationErrorModal from "./modals/ValidationErrorModal";
import NewTreeConfirmationModal from "./modals/NewTreeConfirmationModal";
// import NodeModal from "./modals/NodeModal";
// import PlaceholderModal from "./modals/PlaceholderModal";
// import DeleteModal from "./modals/DeleteModals";
// import PreviewModal from "./modals/PreviewModal";
// import StandaloneModal from "./modals/StandaloneModal";

// Memoize node and edge types to prevent unnecessary re-renders
const nodeTypes: NodeTypes = {
  custom: CustomNode,
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

// Helper function to generate unique condition IDs
const generateConditionId = () => `condition_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const ConversationTree: React.FC = () => {
  const { apiKey, companyId } = usePermissions();
  const [searchParams] = useSearchParams();

  
  // Load saved data from localStorage only if no tree ID in URL - wrap in useMemo to prevent reloading on every render
  const savedData = useMemo(() => {
    const treeId = searchParams.get('id');
    
    // If there's a tree ID in URL, start with blank state instead of localStorage
    if (treeId) {
      return getBlankInitialState();
    }
    
    return loadSavedData();
  }, [searchParams]); // Depend on searchParams to re-evaluate when URL changes
  
  // Extract initial nodes and edges from saved data for convenience
  const { nodes: initialNodes, edges: initialEdges } = savedData;

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  

  
  // Debug: Log when nodes change
  // React.useEffect(() => {
  //   console.log("Nodes state changed:", nodes);
  // }, [nodes]);
  
  // Sync node ID counter with existing nodes to prevent conflicts
  React.useEffect(() => {
    syncNodeIdCounterWithExistingNodes(nodes);
  }, [nodes]);
  
  const [edges, setEdges, onEdgesChange] = useEdgesState(savedData.edges);
  
  // Use modal states hook to manage all modal states
  const {
    modalState,
    setModalState,
    placeholderModalState,
    setPlaceholderModalState,
    deleteModalState,
    setDeleteModalState,
    previewModalState,
    setPreviewModalState,
    standaloneModalState,
    setStandaloneModalState,
    isResetModalVisible,
    setIsResetModalVisible,
    isImportModalVisible,
    setIsImportModalVisible,
    isNewTreeModalVisible,
    setIsNewTreeModalVisible,
    searchText,
    setSearchText,
    resetModalState,
    resetPlaceholderModalState,
    resetDeleteModalState,
    resetStandaloneModalState,
  } = useModalStates();
  
  // Use tree state hook to manage core tree state
  const {
    globalVariableRegistry,
    setGlobalVariableRegistry,
    identifiers,
    setIdentifiers,
    clipboard,
    setClipboard,
    contextMenu,
    setContextMenu,
    canvasContextMenu,
    setCanvasContextMenu,
    actionHistory,
    setActionHistory,
    nodeDimensions,
    setNodeDimensions,
    manuallyMovedNodes,
    setManuallyMovedNodes,
  } = useTreeState();

  // Decision variables registry state
  const [decisionVariablesRegistry, setDecisionVariablesRegistry] = useState<DecisionVariablesRegistry>({});
  
  // State to track globally edited tool calls (for sidebar display)
  // REMOVED: Global tool calls state is no longer needed
  
  // Additional state that wasn't moved to hooks
  const [isFirstNode, setIsFirstNode] = useState(false);
  const [shouldRepositionAfterNodeAddition, setShouldRepositionAfterNodeAddition] = useState(false);
  const [isMiniMapVisible, setIsMiniMapVisible] = useState(true);
  
  // Load tree modal state
  const [isLoadModalVisible, setIsLoadModalVisible] = useState(false);
  
  // Save tree modal state
  const [isSaveTreeModalVisible, setIsSaveTreeModalVisible] = useState(false);
  
  // Loaded tree metadata state
  const [loadedTreeMetadata, setLoadedTreeMetadata] = useState<{
    id: number;
    conversation_flow_name: string;
    application_name: string;
    intent_name: string;
    conversation_flow_version: number;
    is_latest: boolean;
    status?: string;
  } | null>(null);
  
  // Tree status state
  const [currentTreeStatus, setCurrentTreeStatus] = useState<'UNSAVED' | 'SAVED' | 'READY_FOR_FINE_TUNE' | 'RUNNING' | 'FAILED' | 'COMPLETED'>('UNSAVED');
  const [currentTreeName, setCurrentTreeName] = useState<string>('Untitled');
  
  // System prompt state
  const [systemPrompt, setSystemPrompt] = useState<string>("");
  
  // Subscription management for preventing memory leaks
  const subscriptionsRef = useRef<any[]>([]);
  
  // Cleanup subscriptions on unmount
  useEffect(() => {
    return () => {
      subscriptionsRef.current.forEach(subscription => {
        if (subscription && typeof subscription.unsubscribe === "function") {
          subscription.unsubscribe();
        }
      });
      subscriptionsRef.current = [];
    };
  }, []);
  

  
  // REMOVED: Global tool calls registry is no longer needed
  // ToolCallsTab now reads directly from nodes and groups identical tool calls
  
  // Use refs and instance hook to manage refs and ReactFlow instance
  const {
    initMessageLogged,
    mountedRef,
    initializationRef,
    initializationRecorded,
    reactFlowInstance,
    resizeTimeoutRef,
    onInit,
  } = useRefsAndInstance();
  
  if (!initMessageLogged.current) {
    initMessageLogged.current = true;
  }

  
  // Add effect to track mounting/unmounting
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      initializationRef.current = false;
      initializationRecorded.current = false;
    };
  }, []);
  
  // Initialize saved data into state on mount
  useEffect(() => {
    if (savedData.variables && Object.keys(savedData.variables).length > 0) {
      setGlobalVariableRegistry(savedData.variables);
    }
    if (savedData.decisionVariables && Object.keys(savedData.decisionVariables).length > 0) {
      setDecisionVariablesRegistry(savedData.decisionVariables);
    }
    if (savedData.identifiers && savedData.identifiers.length > 0) {
      setIdentifiers(savedData.identifiers);
    }
    
    // Update all existing edges to use custom type
    setEdges(currentEdges => 
      currentEdges.map(edge => ({
        ...edge,
        type: "custom"
      }))
    );
  }, [savedData, setGlobalVariableRegistry, setDecisionVariablesRegistry, setIdentifiers, setEdges]);
  
  // REMOVED: Global tool calls initialization is no longer needed
  // ToolCallsTab now reads directly from nodes
  
  // Initialize isFirstNode properly based on loaded data
  useEffect(() => {
    if (nodes.length === 1 && nodes[0].data.isPlaceholder) {
      setIsFirstNode(true);
    } else if (nodes.length > 1 || (nodes.length === 1 && !nodes[0].data.isPlaceholder)) {
      setIsFirstNode(false);
    }
  }, [nodes.length, nodes, setIsFirstNode]);
  
  // Variable placeholder state - using placeholders hook state for modal but keeping local placeholders state
  const [placeholders, setPlaceholders] = useState<Record<string, VariablePlaceholder>>({});
  
  // Use action history hook
  const { saveToHistory: originalSaveToHistory, undoLastAction } = useActionHistory(
    nodes,
    edges,
    actionHistory,
    setActionHistory,
    setNodes,
    setEdges
  );

  // Wrapper function that marks tree as unsaved when changes are made
  const saveToHistory = useCallback((actionType: "add" | "delete" | "paste") => {
    originalSaveToHistory(actionType);
    // Mark tree as unsaved when any change is made
    if (currentTreeStatus === 'SAVED') {
      setCurrentTreeStatus('UNSAVED');
    }
  }, [originalSaveToHistory, currentTreeStatus, setCurrentTreeStatus]);
  
  // Get all descendants of a node (shared between nodeOperations and copyPasteOperations)
  const getNodeDescendants = useCallback((nodeId: string, allNodes: Node[], allEdges: Edge[]): string[] => {
    const descendants: string[] = [];
    const visited = new Set<string>();
    
    const traverse = (currentId: string) => {
      if (visited.has(currentId)) return;
      visited.add(currentId);
      
      const childrenIds = getNodeChildren(currentId, allEdges);
      descendants.push(...childrenIds);
      
      childrenIds.forEach(childId => {
        traverse(childId);
      });
    };
    
    traverse(nodeId);
    return descendants;
  }, [getNodeChildren]);

  // useLayoutOperations will be called after useNodeOperations to get calculateSmartPosition

  // Function to reposition temporary decision children when decision node is created
  const repositionTemporaryDecisionChildren = useCallback((decisionNodeId: string, retryCount: number = 0) => {
    const tempChildren = (window as any).tempDecisionChildren || [];
    console.log(`[DEBUG] repositionTemporaryDecisionChildren called with:`, {
      decisionNodeId,
      tempChildrenCount: tempChildren.length,
      tempChildren: tempChildren,
      retryCount
    });
    
    if (tempChildren.length === 0) {
      console.log(`[DEBUG] No temporary children to reposition`);
      return;
    }
    
    console.log(`[DEBUG] Looking for decision node with ID:`, decisionNodeId);
    
    // Use setNodes callback to get the most current nodes state
    setNodes(currentNodes => {
      console.log(`[DEBUG] Available nodes:`, currentNodes.map(n => ({ id: n.id, entityType: n.data.entityType, position: n.position })));
      
      const decisionNode = currentNodes.find(n => n.id === decisionNodeId);
      
      if (!decisionNode) {
        if (retryCount < 3) {
          console.log(`[DEBUG] Decision node ${decisionNodeId} not found, retrying in 200ms (attempt ${retryCount + 1}/3)`);
          setTimeout(() => repositionTemporaryDecisionChildren(decisionNodeId, retryCount + 1), 200);
        } else {
          console.error(`[DEBUG] Decision node ${decisionNodeId} not found after 3 retries, giving up`);
        }
        return currentNodes; // Return unchanged nodes
      }
      
      console.log(`[DEBUG] Found decision node:`, { id: decisionNode.id, position: decisionNode.position, entityType: decisionNode.data.entityType });
      
      // Reposition each temporary child relative to the decision node
      const updatedNodes = currentNodes.map(node => {
        const tempChild = tempChildren.find((tc: any) => tc.nodeId === node.id);
        if (!tempChild) return node;
        
        // Calculate proper position relative to decision node
        const HORIZONTAL_SPACING = 150;
        const VERTICAL_SPACING = 180;
        const xOffset = tempChild.pathType === 'true' ? -HORIZONTAL_SPACING : HORIZONTAL_SPACING;
        
        const newPosition = {
          x: decisionNode.position.x + xOffset,
          y: decisionNode.position.y + VERTICAL_SPACING
        };
        
        console.log(`[DEBUG] Repositioning temp child ${node.id} from ${JSON.stringify(node.position)} to ${JSON.stringify(newPosition)}`);
        
        return {
          ...node,
          position: newPosition
        };
      });
      
      // Clear the temporary children
      (window as any).tempDecisionChildren = [];
      
      return updatedNodes;
    });
    
    return; // Exit early since we handled everything in the setNodes callback
  }, [nodes, setNodes]);

  // Add these functions before updateNodeHandlers to avoid dependency issues
  const showDeleteNodeConfirmation = useCallback((nodeId: string) => {
    setDeleteModalState((prev: DeleteModalState) => ({...prev, nodeToDeleteFromMenu: nodeId}));
    setDeleteModalState((prev: DeleteModalState) => ({...prev, deleteChildren: false})); // Reset checkbox state
    setDeleteModalState((prev: DeleteModalState) => ({...prev, isDeleteNodeModalVisible: true}));
  }, [setDeleteModalState]);

  const showDeleteBranchConfirmation = useCallback((nodeId: string) => {
    setDeleteModalState((prev: DeleteModalState) => ({...prev, nodeToDeleteFromMenu: nodeId}));
    setDeleteModalState((prev: DeleteModalState) => ({...prev, deleteChildren: true})); // Set to delete children by default for branch
    setDeleteModalState((prev: DeleteModalState) => ({...prev, isDeleteBranchModalVisible: true}));
  }, [setDeleteModalState]);

  // Use duplicate identifier detection hook
  const {
    getDuplicateIdentifiers,
    isIdentifierInUse,
    getAvailableIdentifiers,
    getDisabledIdentifiers,
    hasChildWithIdentifier
  } = useDuplicateIdentifierDetection(nodes, edges);

  
  const updateNodeHandlers = useCallback((currentEdges?: Edge[]) => {
    // Use provided edges or fall back to current edges state
    const edgesToUse = currentEdges || edges;
    
    // Get duplicate identifiers for this update
    const currentDuplicateIdentifiers = getDuplicateIdentifiers();
    
    setNodes((nds: Node[]) => {
      const updatedNodes = nds.map((node: Node) => {
        const children = getNodeChildren(node.id, edgesToUse);
        
        // Check if this node has a duplicate identifier
        const nodeDuplicateInfo = node.data.identifier ? 
          currentDuplicateIdentifiers.find((dup: {identifier: string, nodeIds: string[]}) => dup.identifier === node.data.identifier) : null;
        
        // Auto-define missing decision path placeholders
        let updatedPlaceholders = { ...node.data.placeholders };
        
        if (node.data.content && (node.data.content.includes('{true_path}') || node.data.content.includes('{false_path}'))) {
          const placeholderRegex = /\{((?:true|false)_path)\}/g;
          let match;
          while ((match = placeholderRegex.exec(node.data.content)) !== null) {
            const placeholderName = match[1];
            if (!updatedPlaceholders[placeholderName] && !globalVariableRegistry[placeholderName]) {
              // Auto-create the missing placeholder
              const pathType = placeholderName.startsWith('true') ? 'true' : 'false';
              const autoPlaceholder = {
                name: placeholderName,
                values: [`This is the ${pathType} path response`],
                persist: false
              };
              updatedPlaceholders[placeholderName] = autoPlaceholder;
              
              // Also add to global registry for future use
              setGlobalVariableRegistry((prev: any) => ({
                ...prev,
                [placeholderName]: autoPlaceholder
              }));
            }
          }
        }
        
        // CRITICAL: Preserve tool calls exactly as they are - create deep copies to prevent reference sharing
        const preservedToolCalls = node.data.toolCalls ? node.data.toolCalls.map((tc: any) => ({
          name: String(tc.name),
          description: tc.description ? String(tc.description) : "",
          parameters: tc.parameters?.map((param: any) => ({
            name: String(param.name),
            value: String(param.value)
          })) || [],
          responseVariables: tc.responseVariables?.map((rv: any) => ({
            name: String(rv.name),
            values: Array.isArray(rv.values) ? rv.values.map((v: any) => String(v)) : []
          })) || []
        })) : undefined;
        
        return {
          ...node,
          data: {
            ...node.data,
            placeholders: updatedPlaceholders, // Use updated placeholders with auto-defined decision paths
            globalRegistry: globalVariableRegistry, // Inject the global registry
            toolCalls: preservedToolCalls, // Explicitly preserve tool calls with deep copies
            hasChildren: children.length > 0,
            duplicateIdentifierInfo: nodeDuplicateInfo, // Add duplicate identifier info
            onNodeResize: handleNodeResize, // Add the resize handler
            onPlaceholderClick: () => {
              if (node.data.isPlaceholder) {
                // Generate a unique identifier for the first node
                const allCurrentIdentifiers = [
                  ...identifiers,
                  ...nodes.filter(n => n.data.identifier && !n.data.isPlaceholder).map(n => n.data.identifier)
                ].filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
                const newIdentifier = generateUniqueIdentifier(allCurrentIdentifiers);
                
                setModalState((prev: ModalState) => ({
                  ...prev,
                  sourceNodeId: node.id,
                  modalTitle: "Create First Node",
                  newNodeIdentifier: newIdentifier,
                  visible: true
                }));
                // For first node, allow choosing entity type
                setModalState((prev: ModalState) => ({...prev, newNodeEntityType: "USER"}));
              }
            },
            onAddNode: (entityType: "USER" | "BOT" | "DECISION") => {
              // Generate a unique identifier for the new node
              const allCurrentIdentifiers = [
                ...identifiers,
                ...nodes.filter(n => n.data.identifier && !n.data.isPlaceholder).map(n => n.data.identifier)
              ].filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
              const newIdentifier = generateUniqueIdentifier(allCurrentIdentifiers);
              
              // Populate form fields with blank data for new node
              setModalState((prev: ModalState) => ({
                ...prev,
                visible: true,
                modalTitle: "Add New Node",
                sourceNodeId: node.id,
                newNodeEntityType: entityType,
                newNodeContent: "",
                newNodePrefix: "",
                newNodeSuffix: "",
                decisionData: {
                  conditions: [{ id: generateConditionId(), variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
                  logicType: "all",
                  truePath: null,
                  falsePath: null
                },
                newNodeIdentifier: newIdentifier,
                nextNodeId: "", // Always start with blank nextNodeId
                nextNodeIds: [],
                isDestinationNode: false,
                isEditMode: false,
                nodeToEdit: null,
                hasToolCall: false,
                toolCalls: []
              }));
            },
            onPreviewConversation: () => {
              if (!node.data.isPlaceholder) {
                handlePreviewConversation(node.id);
              }
            },
            onEditPlaceholder: (placeholderName: string) => {
              setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, editNodeId: node.id}));
              setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, currentPlaceholder: placeholderName}));
              
              // Check if this is a reference to a global variable
              if (globalVariableRegistry[placeholderName] && !node.data.placeholders?.[placeholderName]) {
                // This is a reference to a global variable
                setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, isNewVariable: false}));
              } else {
                // This is either a new variable or a local variable
                setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, isNewVariable: node.data.placeholders?.[placeholderName] ? false : true}));
              }
              
              // Initialize placeholder if it doesn't exist in both local and global registry
              if (!node.data.placeholders?.[placeholderName] && !globalVariableRegistry[placeholderName]) {
                const newPlaceholders = {
                  ...(node.data.placeholders || {}),
                  [placeholderName]: { name: placeholderName, values: [], persist: false }
                };
                
                // Update the node with the new placeholder
                setNodes((nds: Node[]) => 
                  nds.map((n: Node) => 
                    n.id === node.id 
                      ? { ...n, data: { ...n.data, placeholders: newPlaceholders } } 
                      : n
                  )
                );
              }
              
              setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, visible: true}));
            },
            onEditNode: () => {
              
              // Prepare tool call data for BOT nodes
              let toolCallState: { hasToolCall: boolean; toolCalls: ToolCall[] } = { hasToolCall: false, toolCalls: [] };
              if (node.data.entityType === "BOT" && node.data.toolCalls && node.data.toolCalls.length > 0) {
                toolCallState = {
                  hasToolCall: true,
                  toolCalls: node.data.toolCalls || []
                };
              }
              
              // Find the next node connection for non-DECISION nodes
              let nextNodeId = "";
              let nextNodeIds: string[] = [];
              if (node.data.entityType !== "DECISION") {
                const outgoingEdges = edges.filter(edge => edge.source === node.id);
                if (outgoingEdges && outgoingEdges.length > 0) {
                  nextNodeId = outgoingEdges[0].target;
                  nextNodeIds = outgoingEdges.map(e => e.target);
                }
              }
              
              // Populate form fields with current node data in a single call
              setModalState((prev: ModalState) => ({
                ...prev,
                visible: true,
                modalTitle: "Edit Node",
                sourceNodeId: null,
                newNodeEntityType: node.data.entityType,
                newNodeContent: node.data.content,
                newNodePrefix: node.data.prefix || "",
                newNodeSuffix: node.data.suffix || "",
                decisionData: node.data.decisionData || {
                  conditions: [{ id: generateConditionId(), variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
                  logicType: "all",
                  truePath: null,
                  falsePath: null
                },
                newNodeIdentifier: node.data.identifier || "",
                nextNodeId: nextNodeId,
                nextNodeIds: nextNodeIds,
                isDestinationNode: node.data.isDestinationNode || false,
                potentialStart: node.data.potentialStart || false,
                variableAssignments: node.data.variableAssignments || [],
                isEditMode: true,
                nodeToEdit: node.id,
                ...toolCallState
              }));
            },
            onDeleteNode: () => {
              // Use the same delete confirmation as right-click delete
              showDeleteNodeConfirmation(node.id);
            },
            // Add handler for context menu
            onContextMenu: (e: React.MouseEvent) => {
              e.preventDefault();
              if (node.data.isPlaceholder) return; // No context menu for placeholder nodes
              
              setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                nodeId: node.id
              });
            }
          },
        };
      });
      
      // Always return updated nodes because handlers need to be refreshed with current closures
      return updatedNodes;
    });
  }, [setNodes, globalVariableRegistry, edges, showDeleteNodeConfirmation, getDuplicateIdentifiers, identifiers]);





  // Replace the useEffect for updateNodeHandlers to further reduce renders
  useEffect(() => {
    // Skip until fully mounted and initialized
    if (!mountedRef.current) return;
    
    // Use setTimeout to debounce and avoid render loops
    const timeoutId = setTimeout(() => {
      // Just update node handlers, never reposition automatically
      updateNodeHandlers();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [nodes, edges, globalVariableRegistry]);

  // Track previous registry size to detect imports
  const prevRegistrySizeRef = useRef(0);
  
  // Separate effect to handle immediate updates when globalVariableRegistry changes significantly
  // This is specifically to fix the import bug where placeholder validation fails
  useEffect(() => {
    if (!mountedRef.current) return;
    
    const currentSize = Object.keys(globalVariableRegistry).length;
    const prevSize = prevRegistrySizeRef.current;
    
    // Only trigger immediate update if this looks like a bulk import
    // (significant increase in registry size, like going from 0 to multiple items)
    if (currentSize > prevSize && (currentSize - prevSize) > 1) {
      // Force immediate update without debouncing for imports
      updateNodeHandlers();
    }
    
    prevRegistrySizeRef.current = currentSize;
  }, [globalVariableRegistry]);

  // Update content when it changes to detect placeholders
  useEffect(() => {
    if (modalState.newNodeContent) {
      const detectedPlaceholders = extractPlaceholders(modalState.newNodeContent);
      
      // Initialize any new placeholders
      const updatedPlaceholders = { ...placeholders };
      
      detectedPlaceholders.forEach((name: string) => {
        if (!updatedPlaceholders[name] && !globalVariableRegistry[name]) {
          updatedPlaceholders[name] = { name, values: [], persist: false };
        }
      });
      
      setPlaceholders(updatedPlaceholders);
    }
  }, [modalState.newNodeContent, globalVariableRegistry]);

  // Reset conversation tree to initial state
  const handleResetConversation = () => {
    setIsResetModalVisible(true);
  };

  // Handle reset confirmation
  const handleResetConfirm = () => {
    // Preserve current tree metadata (name and version) if it exists
    const preservedMetadata = loadedTreeMetadata ? {
      conversation_flow_name: loadedTreeMetadata.conversation_flow_name,
      application_name: loadedTreeMetadata.application_name,
      intent_name: loadedTreeMetadata.intent_name,
      conversation_flow_version: loadedTreeMetadata.conversation_flow_version,
      id: loadedTreeMetadata.id,
      is_latest: loadedTreeMetadata.is_latest,
      status: loadedTreeMetadata.status
    } : null;
    
    // Get truly blank initial state (ignores localStorage)
    const blankState = getBlankInitialState();
    
    setNodes(blankState.nodes);
    setEdges(blankState.edges);
    setGlobalVariableRegistry(blankState.variables);
    setDecisionVariablesRegistry(blankState.decisionVariables);
    setIdentifiers(blankState.identifiers);
    setIsFirstNode(blankState.isFirstNode);
    setSystemPrompt("");
    
    // Preserve tree metadata if it exists, otherwise reset to null
    setLoadedTreeMetadata(preservedMetadata);
    
    // Reset tree status but preserve tree name if metadata exists
    setCurrentTreeStatus('UNSAVED');
    if (preservedMetadata) {
      setCurrentTreeName(preservedMetadata.conversation_flow_name);
    } else {
      setCurrentTreeName('Untitled');
    }
    
    // Clear interjection groups
    clearGroups();
    
    // Clear any auto-saved data from localStorage to ensure truly blank state
    clearAutoSavedData();
    
    // Reset the camera view after a short delay to ensure state is updated
    setTimeout(() => {
      // Make sure to call updateNodeHandlers to attach event handlers to the initial node
      updateNodeHandlers();
      
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView({ padding: 0.25 });
      }
      
      // Show success notification
      const treeName = preservedMetadata ? preservedMetadata.conversation_flow_name : 'conversation';
      toast.success(`${treeName} has been reset to blank canvas successfully`);
    }, 50);
    
    setIsResetModalVisible(false);
  };

  // Handle reset cancellation
  const handleResetCancel = () => {
    setIsResetModalVisible(false);
  };

  // Handle new tree creation
  const handleNewTree = () => {
    // Check if there are unsaved changes based on tree status
    if (currentTreeStatus !== 'SAVED') {
      setIsNewTreeModalVisible(true);
    } else {
      // Tree is saved, proceed with new tree directly
      handleNewTreeConfirm();
    }
  };

  // Handle new tree confirmation (discard changes and create new tree)
  const handleNewTreeConfirm = () => {
    const blankState = getBlankInitialState();
    setNodes(blankState.nodes);
    setEdges(blankState.edges);
    setGlobalVariableRegistry(blankState.variables);
    setDecisionVariablesRegistry(blankState.decisionVariables);
    setIdentifiers(blankState.identifiers);
    setIsFirstNode(blankState.isFirstNode);
    setSystemPrompt("");
    setLoadedTreeMetadata(null);
    
    // Reset tree status
    setCurrentTreeStatus('UNSAVED');
    setCurrentTreeName('Untitled');
    
    // Clear interjection groups
    clearGroups();
    
    // Reset node ID counter
    resetNodeIdCounter();
    
    // Clear cached tree data
    localStorage.removeItem("maitai-conversation-tree-cache");
    console.log("[Debug] 🗑️ Tree cache cleared (new tree created)");
    
    // Reset the camera view after a short delay to ensure state is updated
    setTimeout(() => {
      // Make sure to call updateNodeHandlers to attach event handlers to the initial node
      updateNodeHandlers();
      
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView({ padding: 0.25 });
      }
      
      // Show success notification
      toast.success("New conversation tree created successfully");
    }, 50);
    
    setIsNewTreeModalVisible(false);
  };

  // Handle new tree cancellation
  const handleNewTreeCancel = () => {
    setIsNewTreeModalVisible(false);
  };

  // Handle automatic new tree creation when new=true parameter is present
  useEffect(() => {
    const isNewTree = searchParams.get('new') === 'true';
    if (isNewTree) {
      // Call the same function that the canvas "New Tree" button uses
      handleNewTreeConfirm();
      
      // Remove the new=true parameter from URL to prevent re-triggering
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('new');
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []); // Empty dependency array - only run once on mount

  // onConnect callback will be defined after useNodeOperations hook


  
  // Add new identifier
  const addIdentifier = (value: string) => {
    if (!value || identifiers.includes(value)) {
      return;
    }
    setIdentifiers(prevIdentifiers => [...prevIdentifiers, value]);
  };

  // Add example to placeholder
  const addExampleToPlaceholder = () => {
    if (!placeholderModalState.newExample || !placeholderModalState.currentPlaceholder) return;
    
    const newExample = placeholderModalState.newExample.trim();
    if (!newExample) return;
    
    // Check if we're editing a global variable
    const isGlobalVariable = !placeholderModalState.isNewVariable && globalVariableRegistry[placeholderModalState.currentPlaceholder];
    
    if (isGlobalVariable) {
      const currentExamples = globalVariableRegistry[placeholderModalState.currentPlaceholder].values || [];
      
      // Allow case-sensitive duplicates (Michael and michael are different)
      if (currentExamples.includes(newExample)) {
        setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, newExample: ""}));
        return;
      }
      
      // Update the global registry
      const updatedRegistry = {
        ...globalVariableRegistry,
        [placeholderModalState.currentPlaceholder]: {
          ...globalVariableRegistry[placeholderModalState.currentPlaceholder],
          values: [...currentExamples, newExample]
        }
      };
      setGlobalVariableRegistry(updatedRegistry);
    } else {
      // Find the node
      const node = nodes.find(n => n.id === placeholderModalState.editNodeId);
      if (!node) return;
      
      const currentPlaceholderData = node.data.placeholders?.[placeholderModalState.currentPlaceholder] || { 
        name: placeholderModalState.currentPlaceholder, 
        values: [] 
      };
      
      // Allow case-sensitive duplicates (Michael and michael are different)
      if (currentPlaceholderData.values.includes(newExample)) {
        setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, newExample: ""}));
        return;
      }
      
      // Update placeholder examples
      const updatedPlaceholder = {
        ...currentPlaceholderData,
        values: [...currentPlaceholderData.values, newExample]
      };
      
      // Update node placeholders
      setNodes(nds => 
        nds.map(n => 
          n.id === placeholderModalState.editNodeId 
            ? { 
                ...n, 
                data: { 
                  ...n.data, 
                  placeholders: {
                    ...n.data.placeholders,
                    [placeholderModalState.currentPlaceholder]: updatedPlaceholder
                  } 
                } 
              } 
            : n
        )
      );
      
      // Also add to global registry for future reference
      setGlobalVariableRegistry((prev: any) => ({
        ...prev,
        [placeholderModalState.currentPlaceholder]: updatedPlaceholder
      }));
    }
    
    setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, newExample: ""}));
  };

  // Remove example from placeholder by index
  const removeExampleFromPlaceholder = (indexToRemove: number) => {
    if (!placeholderModalState.currentPlaceholder) return;
    
    // Check if we're editing a global variable
    const isGlobalVariable = !placeholderModalState.isNewVariable && globalVariableRegistry[placeholderModalState.currentPlaceholder];
    
    if (isGlobalVariable) {
      // Update the global registry
      const updatedExamples = globalVariableRegistry[placeholderModalState.currentPlaceholder].values.filter(
        (_: string, index: number) => index !== indexToRemove
      );
      
      setGlobalVariableRegistry((prev: any) => ({
        ...prev,
        [placeholderModalState.currentPlaceholder]: {
          ...prev[placeholderModalState.currentPlaceholder],
          values: updatedExamples
        }
      }));
    } else {
      // Find the node
      const node = nodes.find(n => n.id === placeholderModalState.editNodeId);
      if (!node) return;
      
      const currentPlaceholderData = node.data.placeholders?.[placeholderModalState.currentPlaceholder];
      if (!currentPlaceholderData) return;
      
      // Filter out the example to remove by index
      const updatedExamples = currentPlaceholderData.values.filter(
        (_: string, index: number) => index !== indexToRemove
      );
      
      // Update node placeholders
      setNodes(nds => 
        nds.map(n => 
          n.id === placeholderModalState.editNodeId 
            ? { 
                ...n, 
                data: { 
                  ...n.data, 
                  placeholders: {
                    ...n.data.placeholders,
                    [placeholderModalState.currentPlaceholder]: {
                      ...currentPlaceholderData,
                      values: updatedExamples
                    }
                  } 
                } 
              } 
            : n
        )
      );
      
      // Also update global registry
      setGlobalVariableRegistry((prev: any) => ({
        ...prev,
        [placeholderModalState.currentPlaceholder]: {
          ...prev[placeholderModalState.currentPlaceholder],
          values: updatedExamples
        }
      }));
    }
  };

  const handlePlaceholderModalCancel = () => {
    resetPlaceholderModalState();
  };

  // Get current examples for the selected placeholder
  const getCurrentExamples = () => {
    if (!placeholderModalState.currentPlaceholder) return [];
    
    // Check if this is a reference to a global variable
    const isGlobalVariable = !placeholderModalState.isNewVariable && globalVariableRegistry[placeholderModalState.currentPlaceholder];
    
    if (isGlobalVariable) {
      return globalVariableRegistry[placeholderModalState.currentPlaceholder]?.values || [];
    }
    
    // Otherwise get from the node
    if (!placeholderModalState.editNodeId) return [];
    const node = nodes.find(n => n.id === placeholderModalState.editNodeId);
    if (!node || !node.data.placeholders) return [];
    
    return node.data.placeholders[placeholderModalState.currentPlaceholder]?.values || [];
  };

  // Handle node movement
  const onNodeDragStop = useCallback((event: React.MouseEvent, node: Node) => {
    // Mark node as manually positioned
    setManuallyMovedNodes((prev: Set<string>) => {
      const newSet = new Set(prev);
      newSet.add(node.id);
      return newSet;
    });
  }, []);

  // --- Dynamic upstream edge styling based on coordinates and theme ---
  const getUpstreamStrokeByTheme = useCallback(() => {
    const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
    // lighter blue in dark mode for contrast, darker blue in light mode
    return isDark ? "#91caff" : "#1677ff";
  }, []);

  const isUpstreamByPosition = useCallback((sourceId: string, targetId: string) => {
    const sourceNode = nodes.find(n => n.id === sourceId);
    const targetNode = nodes.find(n => n.id === targetId);
    if (sourceNode?.position && targetNode?.position) {
      const VERTICAL_THRESHOLD = 12;
      const HORIZONTAL_BACKFLOW_THRESHOLD = -120;
      const dy = targetNode.position.y - sourceNode.position.y;
      const dx = targetNode.position.x - sourceNode.position.x;
      if (dy < -VERTICAL_THRESHOLD) return true;
      if (Math.abs(dy) <= VERTICAL_THRESHOLD && dx < HORIZONTAL_BACKFLOW_THRESHOLD) return true;
    }
    return false;
  }, [nodes]);

  const getDefaultEdgeStyle = useCallback((edge: Edge) => {
    // Reconstruct default styles for decision edges, otherwise no explicit style
    if (edge.id.includes("_true")) {
      return { stroke: "#52c41a", strokeWidth: 2 };
    }
    if (edge.id.includes("_false")) {
      return { stroke: "#f5222d", strokeWidth: 2 };
    }
    return undefined;
  }, []);

  const restyleEdgesForPositions = useCallback(() => {
    setEdges(prevEdges => prevEdges.map((edge: Edge) => {
      const upstream = isUpstreamByPosition(edge.source, edge.target);
      const isDecision = edge.id.includes("_true") || edge.id.includes("_false");
      if (upstream) {
        if (isDecision) {
          const decisionStroke = edge.id.includes("_true") ? "#52c41a" : "#f5222d";
          const dottedDecision = { stroke: decisionStroke, strokeDasharray: "2 6", strokeWidth: 2 } as const;
          return { ...edge, style: { ...getDefaultEdgeStyle(edge), ...dottedDecision } };
        } else {
          const upstreamStyle = { stroke: getUpstreamStrokeByTheme(), strokeDasharray: "2 6", strokeWidth: 2 } as const;
          return { ...edge, style: { ...getDefaultEdgeStyle(edge), ...upstreamStyle } };
        }
      }
      // Not upstream: restore default style for decision edges, otherwise remove dotted styling
      const defaultStyle = getDefaultEdgeStyle(edge);
      if (defaultStyle) {
        return { ...edge, style: defaultStyle };
      }
      if (edge.style && (edge.style as any).strokeDasharray === "2 6") {
        const { style, ...rest } = edge as any;
        const cleanedStyle: any = { ...style };
        delete cleanedStyle.stroke;
        delete cleanedStyle.strokeDasharray;
        delete cleanedStyle.strokeWidth;
        return { ...rest, style: Object.keys(cleanedStyle).length ? cleanedStyle : undefined } as Edge;
      }
      return edge;
    }));
  }, [getDefaultEdgeStyle, isUpstreamByPosition, getUpstreamStrokeByTheme, setEdges]);

  // Recalculate edge styles when nodes move/reposition
  useEffect(() => {
    restyleEdgesForPositions();
  }, [nodes, restyleEdgesForPositions]);

  // Also restyle edges on theme changes (listen for dark class toggles)
  useEffect(() => {
    if (typeof document === "undefined") return;
    const target = document.documentElement;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "class") {
          restyleEdgesForPositions();
          break;
        }
      }
    });
    observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [restyleEdgesForPositions]);

  // Handle context menu visibility
  const hideContextMenu = useCallback(() => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      nodeId: null
    });
  }, []);

  // Handle canvas context menu visibility
  const hideCanvasContextMenu = useCallback(() => {
    setCanvasContextMenu({
      visible: false,
      x: 0,
      y: 0,
      screenPosition: { x: 0, y: 0 }
    });
  }, []);

  // Handle canvas click - hide context menus when clicking on empty space
  const onPaneClick = useCallback(() => {
    // Hide both context menus when clicking on the canvas
    if (contextMenu.visible) {
      hideContextMenu();
    }
    if (canvasContextMenu.visible) {
      hideCanvasContextMenu();
    }
  }, [contextMenu.visible, canvasContextMenu.visible, hideContextMenu, hideCanvasContextMenu]);

  // Add global click listener to hide context menus when clicking outside
  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      // Check if the click is outside the context menus
      const target = event.target as Element;
      const isContextMenuClick = target.closest("[data-context-menu]");
      
      if (!isContextMenuClick) {
        if (contextMenu.visible) {
          hideContextMenu();
        }
        if (canvasContextMenu.visible) {
          hideCanvasContextMenu();
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => {
      document.removeEventListener("click", handleGlobalClick);
    };
  }, [contextMenu.visible, canvasContextMenu.visible, hideContextMenu, hideCanvasContextMenu]);

  // Handle canvas right-click
  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    
    // Hide node context menu if visible
    if (contextMenu.visible) {
      hideContextMenu();
    }
    
    // Get the ReactFlow instance to convert screen coordinates to flow coordinates
    if (!reactFlowInstance.current) return;
    
    const bounds = (event.target as Element).getBoundingClientRect();
    const flowPosition = reactFlowInstance.current.project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });
    
    setCanvasContextMenu({
      visible: true,
      x: flowPosition.x,
      y: flowPosition.y,
      screenPosition: { x: event.clientX, y: event.clientY }
    });
  }, [contextMenu.visible, hideContextMenu, reactFlowInstance]);

  // Handle adding a node at the clicked position
  const handleAddNodeAtPosition = useCallback((position: { x: number; y: number }) => {
    // Generate a unique identifier for the new standalone node
    const allCurrentIdentifiers = [
      ...identifiers,
      ...nodes.filter(n => n.data.identifier && !n.data.isPlaceholder).map(n => n.data.identifier)
    ].filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
    const newIdentifier = generateUniqueIdentifier(allCurrentIdentifiers);
    
    // Open the standalone node modal with default settings
    setStandaloneModalState((prev: StandaloneModalState) => ({
      ...prev,
      visible: true,
      standaloneNodeEntityType: "USER", // Default to USER
      standaloneNodeContent: "",
      standaloneNodePrefix: "",
      standaloneNodeSuffix: "",
              standaloneDecisionData: {
                conditions: [{ id: generateConditionId(), variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
                logicType: "all",
                truePath: null,
                falsePath: null
              },
      standaloneIdentifier: newIdentifier,
      standaloneIsDestination: false,
      standaloneHasToolCall: false,
      standaloneToolCalls: [],
      standaloneVariableAssignments: [], // Initialize variable assignments
    }));
    
    // Store the position for when the modal is submitted
    setStandaloneModalState((prev: StandaloneModalState) => ({
      ...prev,
      nodePosition: position
    }));
  }, [setStandaloneModalState, identifiers, nodes]);

  // Function to find ALL paths from any root to a specific node (for converging branches)
  const findAllPathsToNode = useCallback((targetNodeId: string) => {
    // Create a map of child to parent relationships including Decision node targets
    const parentMap = new Map<string, string[]>();
    
    // Add regular edges
    edges.forEach((edge: Edge) => {
      if (!parentMap.has(edge.target)) {
        parentMap.set(edge.target, []);
      }
      parentMap.get(edge.target)!.push(edge.source);
    });
    
    // Add Decision node routing relationships
    nodes.forEach((node: Node) => {
      if (node.data.entityType === "DECISION" && node.data.decisionData) {
        const decisionData = node.data.decisionData;
        
        // Add true path as potential child
        if (decisionData.truePath) {
          if (!parentMap.has(decisionData.truePath)) {
            parentMap.set(decisionData.truePath, []);
          }
          if (!parentMap.get(decisionData.truePath)!.includes(node.id)) {
            parentMap.get(decisionData.truePath)!.push(node.id);
          }
        }
        
        // Add false path as potential child
        if (decisionData.falsePath) {
          if (!parentMap.has(decisionData.falsePath)) {
            parentMap.set(decisionData.falsePath, []);
          }
          if (!parentMap.get(decisionData.falsePath)!.includes(node.id)) {
            parentMap.get(decisionData.falsePath)!.push(node.id);
          }
        }
      }
    });
    
    // Find all root nodes (nodes with no parents)
    const rootNodes = nodes.filter((node: Node) => !parentMap.has(node.id));
    
    // Recursive function to find all paths from a root to target with improved cycle detection
    const findPathsFromRoot = (rootId: string, targetId: string, currentPath: string[] = [], visited: Set<string> = new Set()): string[][] => {
      // Prevent cycles and excessive depth
      if (visited.has(rootId) || currentPath.length > 20) return []; // Limit path depth to prevent infinite recursion
      
      const newVisited = new Set(visited);
      newVisited.add(rootId);
      
      const newPath = [...currentPath, rootId];
      
      // If we reached the target, return this path
      if (rootId === targetId) {
        return [newPath];
      }
      
      // Get all children of current node (both edges and decision targets)
      const children: string[] = [];
      
      // Add regular edge children
      edges.filter((edge: Edge) => edge.source === rootId).forEach((edge: Edge) => {
        if (!newVisited.has(edge.target)) { // Only add if not already visited
          children.push(edge.target);
        }
      });
      
      // Add Decision node target children
      const currentNode = nodes.find((node: Node) => node.id === rootId);
      if (currentNode?.data.entityType === "DECISION" && currentNode.data.decisionData) {
        const decisionData = currentNode.data.decisionData;
        
        // Add true path target
        if (decisionData.truePath && !children.includes(decisionData.truePath) && !newVisited.has(decisionData.truePath)) {
          children.push(decisionData.truePath);
        }
        
        // Add false path target
        if (decisionData.falsePath && !children.includes(decisionData.falsePath) && !newVisited.has(decisionData.falsePath)) {
          children.push(decisionData.falsePath);
        }
      }
      
      // Recursively find paths through each child
      const allPaths: string[][] = [];
      children.forEach((childId: string) => {
        const childPaths = findPathsFromRoot(childId, targetId, newPath, newVisited);
        allPaths.push(...childPaths);
      });
      
      return allPaths;
    };
    
    // Find all paths from any root to the target
    const allStringPaths: string[][] = [];
    rootNodes.forEach((rootNode: Node) => {
      const pathsFromRoot = findPathsFromRoot(rootNode.id, targetNodeId);
      allStringPaths.push(...pathsFromRoot);
    });
    
    // Convert string paths to node paths
    const allNodePaths = allStringPaths.map((stringPath: string[]) => 
      stringPath.map((nodeId: string) => nodes.find((node: Node) => node.id === nodeId)!).filter(Boolean)
    );
    
    // Deduplicate paths (remove identical paths)
    const uniquePaths: Node[][] = [];
    allNodePaths.forEach((path: Node[]) => {
      const pathSignature = path.map(node => node.id).join("->");
      const isDuplicate = uniquePaths.some(existingPath => 
        existingPath.map(node => node.id).join("->") === pathSignature
      );
      if (!isDuplicate) {
        uniquePaths.push(path);
      }
    });
    
    return uniquePaths;
  }, [nodes, edges]);
  
  // Update the handlePreviewConversation function to ensure a session ID is always set
  const handlePreviewConversation = useCallback((nodeId: string) => {
    const allPaths = findAllPathsToNode(nodeId);
    
    // Set all paths and active path index
    setPreviewModalState((prev: PreviewModalState) => ({
      ...prev, 
      conversationPaths: allPaths,
      conversationPath: allPaths.length > 0 ? allPaths[0] : [], // Backward compatibility
      activePathIndex: 0,
      previewNodeId: nodeId,
      previewSessionId: generateSessionId(),
      visible: true
    }));
  }, [findAllPathsToNode]);
  
  // Also ensure the session ID is reset when the preview is closed
  const handlePreviewModalClose = () => {
    setPreviewModalState((prev: PreviewModalState) => ({...prev, visible: false}));
    setPreviewModalState((prev: PreviewModalState) => ({...prev, previewSessionId: generateSessionId()}));
    
    // Don't reset the conversation path and preview node immediately
    setTimeout(() => {
      setPreviewModalState((prev: PreviewModalState) => ({
        ...prev, 
        conversationPath: [],
        conversationPaths: [],
        activePathIndex: 0,
        previewNodeId: null
      }));
    }, 300); // Small delay for smooth closing animation
  };

  // Function to handle clicking a placeholder in the preview
  const handlePreviewPlaceholderClick = (nodeId: string, placeholderName: string) => {
    setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, editNodeId: nodeId}));
    setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, currentPlaceholder: placeholderName}));

    // Check if this is a reference to a global variable
    if (globalVariableRegistry[placeholderName] && 
        !nodes.find(n => n.id === nodeId)?.data.placeholders?.[placeholderName]) {
      // This is a reference to a global variable
      setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, isNewVariable: false}));
    } else {
      // This is either a new variable or a local variable
      const node = nodes.find(n => n.id === nodeId);
      setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, isNewVariable: node?.data.placeholders?.[placeholderName] ? false : true}));
    }
    
    // Initialize placeholder if it doesn't exist in both local and global registry
    const node = nodes.find(n => n.id === nodeId);
    if (node && !node.data.placeholders?.[placeholderName] && !globalVariableRegistry[placeholderName]) {
      const newPlaceholders = {
        ...(node.data.placeholders || {}),
        [placeholderName]: { name: placeholderName, values: [], persist: false }
      };

      // Update the node with the new placeholder
      setNodes(nds => 
        nds.map(n => 
          n.id === nodeId 
            ? { ...n, data: { ...n.data, placeholders: newPlaceholders } } 
            : n
        )
      );
    }
    
    setPlaceholderModalState((prev: PlaceholderModalState) => ({...prev, visible: true}));
  };

  // Add a useEffect to handle repositioning when the flag is set
  useEffect(() => {
    if (shouldRepositionAfterNodeAddition) {
      // Wait for state updates to be processed
      const repositionAsync = async () => {
        try {
          updateNodeHandlers();
          // FIXED: Never call forceRepositionAllNodes from this effect
          // Decision nodes and their children should maintain their smart positioning
          // Only allow manual repositioning via the reorganize button
          console.log(`[DEBUG] Skipping forceRepositionAllNodes - preserving manual positioning`);
        } catch (error) {
          console.error("Failed to reposition nodes:", error);
        } finally {
          // Reset the flag
          setShouldRepositionAfterNodeAddition(false);
        }
      };
      
      setTimeout(repositionAsync, 200);
    }
  }, [shouldRepositionAfterNodeAddition, updateNodeHandlers, setShouldRepositionAfterNodeAddition, nodes]);

  // Add a function to handle creating a standalone node
  const handleCreateStandaloneNode = () => {
    
    // Check if we have valid content OR valid tool calls
    const hasValidToolCalls = standaloneModalState.standaloneNodeEntityType === "BOT" && standaloneModalState.standaloneHasToolCall && standaloneModalState.standaloneToolCalls.some((tc: any) => tc.name && tc.name.trim());
    const hasValidContent = standaloneModalState.standaloneNodeContent && standaloneModalState.standaloneNodeContent.trim();
    
    
    if (!hasValidContent && !hasValidToolCalls) {
      return;
    }
    
    // Extract placeholders from content
    const detectedPlaceholders = extractPlaceholders(standaloneModalState.standaloneNodeContent);
    const nodePlaceholders: Record<string, VariablePlaceholder> = {};
    
    // Initialize placeholders
    detectedPlaceholders.forEach((name: string) => {
      if (globalVariableRegistry[name]) {
        nodePlaceholders[name] = { name, values: [], persist: false };
      } else {
        nodePlaceholders[name] = { name, values: [], persist: false };
        
        // Add to global registry
        setGlobalVariableRegistry((prev: any) => ({
          ...prev,
          [name]: { name, values: [], persist: false }
        }));
      }
    });
    
    // Add identifier to the list if it's a new one (auto-generated identifiers should always be added)
    if (standaloneModalState.standaloneIdentifier && 
        !identifiers.includes(standaloneModalState.standaloneIdentifier)) {
      setIdentifiers((prev: string[]) => [...prev, standaloneModalState.standaloneIdentifier]);
    }
    
    // Save current state to history
    saveToHistory("add");
    
    // Determine position for the new node
    let newX: number;
    let newY: number;
    
    if (standaloneModalState.nodePosition) {
      // Use the position from canvas context menu
      newX = standaloneModalState.nodePosition.x;
      newY = standaloneModalState.nodePosition.y;
    } else {
      // Find the rightmost position in the current graph to place the new node
      let rightmostX = 0;
      let lowestY = 0;
      
      nodes.forEach((node: Node) => {
        if (node.position.x > rightmostX) {
          rightmostX = node.position.x;
        }
        if (node.position.y > lowestY) {
          lowestY = node.position.y;
        }
      });
      
      // Position the new node to the right if there are existing nodes, or center if it's first after placeholder
      newX = nodes.length > 1 ? rightmostX + NODE_WIDTH + 100 : 500;
      newY = nodes.length > 1 ? lowestY + 100 : 200;
    }
    
    // Check if this is the first real node (replacing placeholder)
    const onlyHasPlaceholder = nodes.length === 1 && nodes[0].data.isPlaceholder;
    
    // Calculate sequential ID outside of node creation to prevent multiple calls
    let sequentialId: string;
    if (onlyHasPlaceholder) {
      // Ensure the first real node gets ID 1
      resetNodeIdCounter();
      sequentialId = getNextNodeId().toString();
    } else {
      sequentialId = getNextNodeId().toString();
    }
    
    // Prepare tool call data if this is a BOT node and has tool call enabled
    let toolCallsData: ToolCall[] = [];
    if (standaloneModalState.standaloneNodeEntityType === "BOT" && standaloneModalState.standaloneHasToolCall) {
      toolCallsData = standaloneModalState.standaloneToolCalls.filter((tc: any) => tc.name && tc.name.trim());
    }
    
    // Create new standalone node
    const newNode: Node = {
      id: sequentialId,
      type: "custom",
      position: { x: newX, y: newY },
      data: {
        label: standaloneModalState.standaloneNodeEntityType,
        content: standaloneModalState.standaloneNodeContent,
        prefix: standaloneModalState.standaloneNodePrefix,
        suffix: standaloneModalState.standaloneNodeSuffix,
        decisionData: standaloneModalState.standaloneDecisionData,
        isPlaceholder: false,
        hasChildren: false,
        entityType: standaloneModalState.standaloneNodeEntityType,
        identifier: standaloneModalState.standaloneIdentifier,
        isDestinationNode: standaloneModalState.standaloneIsDestination,
        potentialStart: standaloneModalState.standalonePotentialStart, // Set potential start status
        isDescendantOfDestination: false,
        placeholders: nodePlaceholders,
        globalRegistry: globalVariableRegistry,
        toolCalls: toolCallsData, // Add tool calls data
        variableAssignments: standaloneModalState.standaloneVariableAssignments, // Add variable assignments
        onPlaceholderClick: () => {},
        onAddNode: () => {},
        onEditPlaceholder: () => {},
        onEditNode: () => {},
        onDeleteNode: () => {},
        onPreviewConversation: () => {},
        onContextMenu: () => {}
      },
    };
    
    if (onlyHasPlaceholder) {
      // Replace the placeholder with the new standalone node
      setNodes([newNode]);
      setEdges([]);
      setIsFirstNode(false);
    } else {
      // Add the standalone node alongside existing nodes
      setNodes(nds => [...nds, newNode]);
    }
    
    // Handle nextNodeId edge creation for standalone nodes (non-DECISION)
    if (standaloneModalState.standaloneNodeEntityType !== "DECISION" && 
        standaloneModalState.standaloneNextNodeId && 
        standaloneModalState.standaloneNextNodeId !== "") {
      setTimeout(() => {
        setEdges(eds => {
          const nextEdgeId = `e${sequentialId}-${standaloneModalState.standaloneNextNodeId}`;
          // Check if edge already exists
          const existingEdge = eds.find(edge => edge.id === nextEdgeId);
          if (existingEdge) {
            return eds; // Don't add duplicate
          }
          
          const nextEdge: Edge = {
            id: nextEdgeId,
            source: sequentialId,
            target: standaloneModalState.standaloneNextNodeId,
            type: "custom",
          };
          
          const updatedEdges = [...eds, nextEdge];
          
          // After edge is added, reposition children for the standalone node (if it becomes a parent)
          console.log(`[DEBUG] Centering: Standalone next field edge added, will reposition children for ${standaloneModalState.standaloneNodeEntityType} node ${sequentialId}`);
          setTimeout(() => {
            setNodes(currentNodes => {
              console.log(`[DEBUG] Centering: Running repositionChildrenCentered for standalone node ${sequentialId} after next node assignment`);
              return repositionChildrenCentered(sequentialId, currentNodes, updatedEdges);
            });
          }, 50);
          
          return updatedEdges;
        });
      }, 100);
    }
    
    // Update node handlers and conditionally trigger repositioning
    setTimeout(() => {
      updateNodeHandlers();
      // Only trigger repositioning if the node was NOT created via canvas context menu (right-click)
      // If nodePosition is set, it means the user right-clicked to place the node at a specific position
      if (!standaloneModalState.nodePosition) {
        setShouldRepositionAfterNodeAddition(true);
      }
    }, 150); // Increased delay to run after edge creation
    
    // Reset modal state
    resetStandaloneModalState();
  };
  
  const handleStandaloneCancel = () => {
    resetStandaloneModalState();
  };
  
  // Add an effect to handle node initialization after reset
  useEffect(() => {
    // Check if we have a placeholder node that needs handlers
    if (isFirstNode && nodes.length === 1 && nodes[0].data.isPlaceholder) {
      // Ensure handlers are applied to the placeholder node
      setTimeout(() => {
        updateNodeHandlers();
      }, 100);
    }
  }, [isFirstNode, nodes.length]);
  




  // Use node operations hook (after all function declarations)
  const {
    handleOk: nodeHandleOk,
    handleCancel: nodeHandleCancel,
    handleDeleteNodeConfirm: nodeHandleDeleteNodeConfirm,
    handleDeleteBranchConfirm: nodeHandleDeleteBranchConfirm,
    handleDeleteModalCancel: nodeHandleDeleteModalCancel,
    calculateSmartPosition,
    repositionChildrenCentered,
    customReorganizeTree
  } = useNodeOperations({
    nodes,
    edges,
    setNodes,
    setEdges,
    modalState,
    deleteModalState,
    placeholders,
    setPlaceholders,
    saveToHistory,
    identifiers,
    setIdentifiers,
    globalVariableRegistry,
    setGlobalVariableRegistry,
    isFirstNode,
    setIsFirstNode,
    setShouldRepositionAfterNodeAddition,
    hasChildWithIdentifier,
    resetModalState,
    resetDeleteModalState,
    getNodeDescendants,
    initialNodes,
    initialEdges,
    updateNodeHandlers,
    setManuallyMovedNodes,
    repositionTemporaryDecisionChildren,
    nodeDimensions
  });

  // Use layout operations hook (after useNodeOperations to get calculateSmartPosition)
  const { 
    forceRepositionAllNodes, 
    handleNodeResize 
  } = useLayoutOperations(
    nodes,
    edges,
    setNodes,
    nodeDimensions,
    manuallyMovedNodes,
    setManuallyMovedNodes,
    setNodeDimensions,
    resizeTimeoutRef,
    calculateSmartPosition
  );

  // Use copy/paste operations hook (after forceRepositionAllNodes is available)
  const { copyNode, copyBranch, pasteNode, pasteBranch } = useCopyPasteOperations({
    nodes,
    edges,
    setNodes,
    setEdges,
    clipboard,
    setClipboard,
    saveToHistory,
    isIdentifierInUse,
    updateNodeHandlers,
    forceRepositionAllNodes,
    getNodeDescendants,
    repositionChildrenCentered
  });
  
  // Define onConnect callback after useNodeOperations to access repositionChildrenCentered
  const onConnect = useCallback(
    (connection: Connection) => {
      const edgeId = `e${connection.source}-${connection.target}`;
      
      setEdges((eds) => {
        // Check if edge already exists
        const existingEdge = eds.find(edge => edge.id === edgeId);
        if (existingEdge) {
          // Edge already exists, don't add duplicate
          return eds;
        }
        
        const newEdge: Edge = {
          id: edgeId,
          source: connection.source!,
          target: connection.target!,
          type: "custom",
          sourceHandle: connection.sourceHandle || "source",
          targetHandle: connection.targetHandle || "target",
        };
        
        const updatedEdges = [...eds, newEdge];
        
        // After edge is added, reposition children for any parent node (except DECISION nodes)
        const parentNode = nodes.find(n => n.id === connection.source);
        if (parentNode && parentNode.data.entityType !== "DECISION") {
          console.log(`[DEBUG] Centering: Connection edge added, will reposition children for ${parentNode.data.entityType} parent ${connection.source}`);
          setTimeout(() => {
            setNodes(currentNodes => {
              console.log(`[DEBUG] Centering: Running repositionChildrenCentered for parent ${connection.source} after connection`);
              return repositionChildrenCentered(connection.source!, currentNodes, updatedEdges);
            });
          }, 50);
        }
        
        return updatedEdges;
      });
    },
    [setEdges, nodes, setNodes, repositionChildrenCentered]
  );
  
  // Update onNodesDelete and onEdgesDelete to save history with optimized repositioning
  const onNodesDelete = useCallback((deleted: Node[]) => {
    if (deleted.length > 0) {
      saveToHistory("delete");
    }
    // Only reposition for smaller trees or when explicitly requested
    setTimeout(async () => {
      try {
        updateNodeHandlers();
        if (nodes.length <= 75) {
          await forceRepositionAllNodes();
        }
      } catch (error) {
        console.error("Failed to reposition after deletion:", error);
      }
    }, 0);
  }, [forceRepositionAllNodes, saveToHistory, nodes.length, updateNodeHandlers]);

  // Handle edge deletion with optimized repositioning
  const onEdgesDelete = useCallback((deleted: Edge[]) => {
    if (deleted.length > 0) {
      saveToHistory("delete");
    }
    // Only reposition for smaller trees or when explicitly requested
    setTimeout(() => {
      updateNodeHandlers();
      if (nodes.length <= 50) {
        forceRepositionAllNodes();
      }
    }, 0);
  }, [forceRepositionAllNodes, saveToHistory, nodes.length]);
  
  // Use config sidebar hook
  const {
    configState,
    // New group-based functions
    startAddingNewGroup,
    startEditingGroup,
    cancelEditing,
    saveInterjectionGroup,
    deleteInterjectionGroup,
    startAddingContent,
    startEditingContent,
    saveContent,
    saveContentForGroup,
    deleteContent,
    clearGroups,
    setGroups,
  } = useConfigSidebar();
  
  // Use manual save hook for comprehensive save functionality
  const { hasUnsavedChanges, lastSavedTimestamp, autoSaveStatus, saveFlow } = useManualSave(
    nodes,
    edges,
    globalVariableRegistry,
    decisionVariablesRegistry,
    identifiers,
    configState.groups
  );



  // Add state for delete interjection group modal
  const [isDeleteInterjectionGroupModalVisible, setIsDeleteInterjectionGroupModalVisible] = useState(false);
  const [interjectionGroupToDelete, setInterjectionGroupToDelete] = useState<InterjectionGroup | null>(null);

  // Add handlers for delete interjection group modal
  const handleDeleteInterjectionGroupConfirm = () => {
    if (interjectionGroupToDelete) {
      deleteInterjectionGroup(interjectionGroupToDelete.id);
    }
    setIsDeleteInterjectionGroupModalVisible(false);
    setInterjectionGroupToDelete(null);
  };

  const handleDeleteInterjectionGroupCancel = () => {
    setIsDeleteInterjectionGroupModalVisible(false);
    setInterjectionGroupToDelete(null);
  };

  const showDeleteInterjectionGroupConfirmation = (group: InterjectionGroup) => {
    setInterjectionGroupToDelete(group);
    setIsDeleteInterjectionGroupModalVisible(true);
  };

  // Function to open edit modal for a specific node (used by Placeholders tab)
  const handleEditNodeFromPlaceholders = useCallback((nodeId: string) => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return;

    // Prepare tool call data for BOT nodes
    let toolCallState: { hasToolCall: boolean; toolCalls: ToolCall[] } = { hasToolCall: false, toolCalls: [] };
    if (node.data.entityType === "BOT" && node.data.toolCalls && node.data.toolCalls.length > 0) {
      toolCallState = {
        hasToolCall: true,
        toolCalls: node.data.toolCalls || []
      };
    }
    
    // Populate form fields with current node data
    setModalState((prev: ModalState) => ({
      ...prev,
      visible: true,
      modalTitle: "Edit Node",
      sourceNodeId: null,
      newNodeEntityType: node.data.entityType,
      newNodeContent: node.data.content,
      newNodePrefix: node.data.prefix || "",
      newNodeSuffix: node.data.suffix || "",
              decisionData: {
                conditions: [{ id: generateConditionId(), variable: "", operator: "==", value: "", type: "value", joiner: "and" }],
                logicType: "all",
                truePath: null,
                falsePath: null
              },
      newNodeIdentifier: node.data.identifier || "",
      isDestinationNode: node.data.isDestinationNode || false,
      potentialStart: node.data.potentialStart || false,
      variableAssignments: node.data.variableAssignments || [],
      isEditMode: true,
      nodeToEdit: node.id,
      ...toolCallState
    }));
  }, [nodes, setModalState]);

  // Handle focusing on a specific node
  const handleNodeFocus = useCallback((nodeId: string) => {
    const nodeToFocus = nodes.find(n => n.id === nodeId);
    if (nodeToFocus && reactFlowInstance.current) {
      reactFlowInstance.current.fitView({
        nodes: [nodeToFocus],
        padding: 0.3,
        duration: 800,
      });
    }
  }, [nodes, reactFlowInstance]);

  // External tool call modal state
  const [externalToolCallModal, setExternalToolCallModal] = useState<{
    visible: boolean;
    toolCall: any;
    nodeId: string;
    isGlobalEdit: boolean;
    originalSignature: string;
  } | null>(null);

  // ============================================================================
  // TOOL CALL MANAGEMENT - COMPLETELY REWRITTEN
  // ============================================================================
  
  /**
   * Updates ALL nodes that have the same tool call signature (GLOBAL UPDATE)
   * Used when editing from the sidebar ToolCallsTab
   */
  const updateAllNodesWithSameToolCall = useCallback((originalSignature: string, updatedToolCall: ToolCall) => {
    
    setNodes(prevNodes => {
      const updatedNodes = prevNodes.map(node => {
        // Only update BOT nodes that have tool calls
        if (node.data.entityType === "BOT" && node.data.toolCalls && node.data.toolCalls.length > 0) {
          const currentToolCall = node.data.toolCalls[0]; // We only support one tool call per node
          const currentSignature = getToolCallSignature(currentToolCall);
          
          // If this node's tool call signature matches the original, update it
          if (currentSignature === originalSignature) {
            return {
              ...node,
              data: {
                ...node.data,
                toolCalls: [{ ...updatedToolCall }] // Replace with updated tool call
              }
            };
          }
        }
        return node; // No change for nodes that don't match
      });
      return updatedNodes;
    });
    
    saveToHistory("add");
  }, [setNodes, saveToHistory]);

  /**
   * Updates ONLY a specific node's tool call (SINGLE NODE UPDATE)
   * Used when editing from individual node modal
   */
  const updateSingleNodeToolCall = useCallback((nodeId: string, updatedToolCall: ToolCall) => {
    
    setNodes(prevNodes => {
      const updatedNodes = prevNodes.map(node => {
        if (node.id === nodeId && node.data.entityType === "BOT") {
          return {
            ...node,
            data: {
              ...node.data,
              toolCalls: [{ ...updatedToolCall }] // Replace with updated tool call
            }
          };
        }
        return node; // No change for other nodes
      });

      return updatedNodes;
    });
    
    saveToHistory("add");
  }, [setNodes, saveToHistory]);

  /**
   * Creates a signature string from a tool call for grouping/comparison
   */
  const getToolCallSignature = useCallback((toolCall: ToolCall): string => {
    if (!toolCall) return "";
    
    const paramsString = toolCall.parameters
      ?.map((p: any) => `${p.name}:${p.value}`)
      .sort() // Sort to ensure consistent ordering
      .join("|") || "";
      
    return `${toolCall.name}|${toolCall.description || ""}|${paramsString}`;
  }, []);

  /**
   * Handle editing tool call from sidebar (GLOBAL UPDATE)
   */
  const handleEditToolCallFromSidebar = useCallback((originalSignature: string, toolCall: ToolCall) => {
    
    // Set up external modal for global editing
    setExternalToolCallModal({
      visible: true,
      toolCall: { ...toolCall },
      nodeId: "", // No specific node for global edits
      isGlobalEdit: true,
      originalSignature
    });
  }, []);

  /**
   * Handle completion of tool call editing (both global and single)
   */
  const handleExternalToolCallEdit = useCallback((nodeId: string, updatedToolCall: ToolCall) => {
    const modal = externalToolCallModal;
    if (!modal) return;
    
    if (modal.isGlobalEdit && modal.originalSignature) {
      // Global update: update all nodes with the original signature

      updateAllNodesWithSameToolCall(modal.originalSignature, updatedToolCall);
    } else if (nodeId) {
      // Single node update: update only the specific node

      updateSingleNodeToolCall(nodeId, updatedToolCall);
    }
    
    // Close the modal
    setExternalToolCallModal(null);
  }, [externalToolCallModal, updateAllNodesWithSameToolCall, updateSingleNodeToolCall]);

  /**
   * Handle cancellation of tool call editing
   */
  const handleExternalToolCallCancel = useCallback(() => {
    setExternalToolCallModal(null);
  }, []);

  // Export flow as JSON file
  const handleExportFlow = useCallback(() => {
    // Check for validation issues and warn user
    const flowValidation = validateFlow(nodes, globalVariableRegistry);
    if (!flowValidation.isValid) {
      toast.warning(`Flow contains ${flowValidation.invalidNodes.length} node(s) with validation issues. Consider fixing these before sharing.`);
    }

    try {
      const flowData = exportFlowData(
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        configState.groups,
        systemPrompt,
        {
          title: "Conversation Flow Export",
          description: `Exported on ${new Date().toLocaleString()}`,
          status: "draft"
        }
      );

      // Create downloadable file
      const blob = new Blob([JSON.stringify(flowData, null, 2)], {
        type: "application/json",
      });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversation_flow_${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Flow exported successfully!");
    } catch (error) {
      toast.error("Failed to export flow");
    }
  }, [nodes, edges, globalVariableRegistry, decisionVariablesRegistry, identifiers, configState.groups, systemPrompt]);

  // Save flow to backend
  const handleSaveToBackend = useCallback(async (metadata: {
    conversation_flow_name: string;
    company_id: string;
    application_ref_name: string;
    action_type: string;
    conversation_flow_version?: string;
    is_latest?: boolean;
  }) => {
    // Validate flow before saving
    const flowValidation = validateFlow(nodes, globalVariableRegistry);
    if (!flowValidation.isValid) {
      setValidationErrors(flowValidation.allIssues);
      setIsValidationErrorModalVisible(true);
      return;
    }

    if (!apiKey) {
      toast.error("API key not available. Please make sure you're logged in.");
      return;
    }

    try {
      const flowData = exportFlowData(
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        configState.groups,
        systemPrompt,
        {
          title: "Conversation Flow Backend Save",
          description: `Saved on ${new Date().toLocaleString()}`,
          status: "draft"
        }
      );

      const backendRequest = convertFlowDataToConversationBuilderRequest(flowData, metadata);
      
      // console.log("Sending backend request:", JSON.stringify(backendRequest, null, 2));
      
      const saveRequest = ConversationBuilderService.saveAndGetPaths(
        backendRequest,
        () => {}, // Loading handler
        apiKey
      );

      const subscription = saveRequest.subscribe({
        next: (response: ConversationBuilderSaveResponse) => {
          const newVersion = response.conversation_flow_version;
                toast.success("Saved successfully");
      // console.log("Save response:", response);
          
          // Update or create loaded tree metadata
          if (loadedTreeMetadata) {
            // Update existing tree metadata
            setLoadedTreeMetadata(prev => prev ? {
              ...prev,
              conversation_flow_version: newVersion,
              is_latest: true
            } : null);
          } else {
            // Create new tree metadata for newly saved tree
            setLoadedTreeMetadata({
              id: parseInt(response.conversation_builder_config_id),
              conversation_flow_name: response.conversation_flow_name,
              application_name: metadata.application_ref_name,
              intent_name: metadata.action_type,
              conversation_flow_version: newVersion,
              is_latest: true,
              status: 'SAVED',
            });
          }
          
          // Update tree status to SAVED and set tree name
          setCurrentTreeStatus('SAVED');
          if (response.conversation_flow_name) {
            setCurrentTreeName(response.conversation_flow_name);
          }
          
          // Clear cached tree since it's now saved to backend
          localStorage.removeItem("maitai-conversation-tree-cache");
          console.log("[Debug] 🗑️ Tree cache cleared (saved to backend)");
          
          // Remove subscription from tracking after completion
          subscriptionsRef.current = subscriptionsRef.current.filter(sub => sub !== subscription);
        },
        error: (error: any) => {
          toast.error(`Failed to save flow: ${error}`);
          
          // Remove subscription from tracking after error
          subscriptionsRef.current = subscriptionsRef.current.filter(sub => sub !== subscription);
        }
      });
      
      // Track subscription for cleanup
      subscriptionsRef.current.push(subscription);
    } catch (error) {
      toast.error("Failed to prepare flow for backend save");
    }
  }, [nodes, edges, globalVariableRegistry, decisionVariablesRegistry, identifiers, configState.groups, apiKey, systemPrompt]);

  // Ref to track if an import just happened
  const importInProgressRef = useRef(false);

  // In handleImportConfirm, set the flag before updating state
  const handleImportConfirm = useCallback((flowData: FlowData) => {
    try {
      saveToHistory("add");
      const importedData = importFlowData(flowData);
      importInProgressRef.current = true; // Set flag
      setNodes(importedData.nodes);
      setEdges(importedData.edges);
      setGlobalVariableRegistry(importedData.variables);
      setDecisionVariablesRegistry(importedData.decisionVariables);
      setIdentifiers(importedData.identifiers);
      setIsFirstNode(importedData.isFirstNode);
      setSystemPrompt(importedData.systemPrompt || ""); // Set system prompt from imported data
      
      // Load interjection groups
      if (importedData.interjectionGroups) {
        setGroups(importedData.interjectionGroups);
      }
      
      toast.success("Flow imported successfully!");
    } catch (error) {
      toast.error("Failed to import flow");
    }
  }, [
    saveToHistory,
    setNodes, 
    setEdges, 
    setGlobalVariableRegistry, 
    setDecisionVariablesRegistry,
    setIdentifiers,
    setIsFirstNode,
    setGroups,
  ]);

  // useEffect to run updateNodeHandlers after import, when globalVariableRegistry changes
  useEffect(() => {
    if (importInProgressRef.current) {
      updateNodeHandlers();
      // FIXED: Don't auto-reposition when loading trees from backend - preserve stored positions
      importInProgressRef.current = false;
    }
  }, [globalVariableRegistry]);

  // Automatically add tool response variables to decision variables registry
  useEffect(() => {
    const newDecisionVariables: Record<string, DecisionVariable> = {};
    
    // Scan global variable registry for tool response variables
    Object.entries(globalVariableRegistry as GlobalVariableRegistry).forEach(([varName, varData]) => {
      // Check if this variable is not already in the decision variables registry
      if (!decisionVariablesRegistry[varName] && varData) {
        // Determine variable type based on the values
        let variableType: 'string' | 'number' | 'boolean' = 'string';
        
        if (varData.values && varData.values.length > 0) {
          const firstValue = varData.values[0];
          
          // Try to determine type from the first value
          if (typeof firstValue === 'boolean' || 
              (typeof firstValue === 'string' && 
               ['true', 'false', 'yes', 'no'].includes(firstValue.toLowerCase()))) {
            variableType = 'boolean';
          } else if (typeof firstValue === 'number' || 
                     (typeof firstValue === 'string' && !isNaN(Number(firstValue)))) {
            variableType = 'number';
          } else {
            variableType = 'string';
          }
        }
        
        newDecisionVariables[varName] = {
          name: varName,
          type: variableType
        };
      }
    });
    
    // Update decision variables registry if we have new variables
    if (Object.keys(newDecisionVariables).length > 0) {
      setDecisionVariablesRegistry(prev => ({
        ...prev,
        ...newDecisionVariables
      }));
      
      console.log(`[ConversationTree] Automatically added ${Object.keys(newDecisionVariables).length} tool response variables to decision registry:`, Object.keys(newDecisionVariables));
    }
  }, [globalVariableRegistry, decisionVariablesRegistry]);

  // Handle import modal cancel
  const handleImportCancel = useCallback(() => {
    setIsImportModalVisible(false);
  }, [setIsImportModalVisible]);

  // Toggle mini-map visibility
  const toggleMiniMap = useCallback(() => {
    setIsMiniMapVisible(prev => !prev);
  }, []);

  // Handle creating a new decision variable from the NodeModal
  const handleCreateDecisionVariable = useCallback((variableData: { name: string; type: string }) => {
    setDecisionVariablesRegistry(currentRegistry => ({
      ...currentRegistry,
      [variableData.name]: {
        name: variableData.name,
        type: variableData.type as any
      }
    }));
    
    // No localStorage persistence - decision variables only exist in current session
  }, []);

  // Handle creating a new node for decision paths
  const handleCreateNodeForDecisionPath = useCallback(async (pathType: 'true' | 'false', sourceNodeId: string, isTemporary: boolean = false): Promise<string> => {
    // Generate a unique identifier for the new node
    const pathIdentifier = `${pathType}_path_${Date.now()}`;
    
    // Add the identifier to the identifiers list
    setIdentifiers((prev: string[]) => [...prev, pathIdentifier]);
    
    // Get the source node to position the new node properly
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    let newPosition = { x: 250, y: 100 };
    
    console.log(`[DEBUG] Looking for source node with ID:`, sourceNodeId, `(type: ${typeof sourceNodeId})`);
    console.log(`[DEBUG] Available nodes:`, nodes.map(n => ({ id: n.id, type: typeof n.id, entityType: n.data.entityType, position: n.position })));
    console.log(`[DEBUG] Found source node:`, sourceNode ? { id: sourceNode.id, position: sourceNode.position, entityType: sourceNode.data.entityType } : null);
    
    if (!sourceNode) {
      console.error(`[DEBUG] SOURCE NODE NOT FOUND! This is why positioning fails.`);
      console.log(`[DEBUG] Trying to find by string conversion...`);
      const sourceNodeByString = nodes.find(node => String(node.id) === String(sourceNodeId));
      console.log(`[DEBUG] Found by string conversion:`, sourceNodeByString ? { id: sourceNodeByString.id, position: sourceNodeByString.position } : null);
    }
    
    if (sourceNode) {
      // Handle temporary positioning for nodes created during decision node creation
      if (isTemporary && sourceNode.data.entityType !== "DECISION") {
        console.log(`[DEBUG] Creating temporary decision path node from ${sourceNode.data.entityType} node`);
        // Position temporarily to the right of the source node, will be repositioned later
        const tempXOffset = 300; // Far to the right so it's visible but clearly temporary
        newPosition = {
          x: sourceNode.position.x + tempXOffset,
          y: sourceNode.position.y + (pathType === 'true' ? 0 : 100) // Stack them vertically
        };
        console.log(`[DEBUG] Temporary positioning:`, newPosition);
      } else {
        // Normal decision node positioning
        if (sourceNode.data.entityType !== "DECISION") {
          console.error(`[DEBUG] ERROR: Trying to create decision paths from a ${sourceNode.data.entityType} node! This should only work with DECISION nodes.`);
        }
        
        const NODE_WIDTH = 280;
        const HORIZONTAL_SPACING = 150;
        const VERTICAL_SPACING = 180;
        
        // DEAD SIMPLE: Check if target nodes exist and have children
        const decisionData = sourceNode.data.decisionData || { truePath: null, falsePath: null, conditions: [] };
      
      // Check if the target node already exists and has children
      let targetNode: Node | undefined = undefined;
      if (pathType === 'true' && decisionData.truePath) {
        targetNode = nodes.find(n => n.data.identifier === decisionData.truePath || n.id === decisionData.truePath);
      } else if (pathType === 'false' && decisionData.falsePath) {
        targetNode = nodes.find(n => n.data.identifier === decisionData.falsePath || n.id === decisionData.falsePath);
      }
      
      if (targetNode) {
        // Node already exists - check if it has children
        const hasChildren = edges.some(edge => edge.source === targetNode!.id);
        if (hasChildren) {
          // Just link to it, don't move it
          console.log(`[DEBUG] Target node ${targetNode.id} has children, just linking without moving`);
          return targetNode.data.identifier || targetNode.id;
        } else {
          // It's a free node, we can reposition it
          console.log(`[DEBUG] Target node ${targetNode.id} is free, will reposition`);
        }
      }
      
      // Position new nodes: first path left, second path right
      const existingPaths = [decisionData.truePath, decisionData.falsePath].filter(Boolean).length;
      let xOffset = -HORIZONTAL_SPACING; // Default to left (first child)
      if (existingPaths === 1) {
        xOffset = HORIZONTAL_SPACING; // Second child goes right
      }
      
      newPosition = {
        x: sourceNode.position.x + xOffset,
        y: sourceNode.position.y + VERTICAL_SPACING
      };
      
        console.log(`[DEBUG] Positioning decision child:`, { 
          pathType, 
          existingPaths, 
          xOffset, 
          newPosition 
        });
      } // End of else block for normal positioning
    }
    
    // Calculate the sequential ID for the new node
    const sequentialId = getNextNodeId().toString();
    
    // Create placeholder for the path content
    const placeholderName = `${pathType}_path`;
    const pathPlaceholder = {
      name: placeholderName,
      values: [`This is the ${pathType} path response`],
      persist: false
    };
    
    // Add to global variable registry
    setGlobalVariableRegistry((prev: any) => ({
      ...prev,
      [placeholderName]: pathPlaceholder
    }));

    // Create the new BOT node
    const newNode: Node = {
      id: sequentialId,
      type: "custom",
      position: newPosition,
      data: {
        label: "BOT",
        content: `{${pathType}_path}`, // Default placeholder content
        prefix: "",
        suffix: "",
        isPlaceholder: false,
        hasChildren: false,
        entityType: "BOT",
        identifier: pathIdentifier,
        isDestinationNode: false,
        potentialStart: false,
        isDescendantOfDestination: false,
        placeholders: {
          [placeholderName]: pathPlaceholder
        },
        globalRegistry: globalVariableRegistry,
        toolCalls: [],
        variableAssignments: [],
        onPlaceholderClick: () => {},
        onAddNode: () => {},
        onEditPlaceholder: () => {},
        onEditNode: () => {},
        onDeleteNode: () => {},
      },
    };

    // Add the node to the graph with position
    console.log(`[DEBUG] BEFORE adding node - position:`, newPosition);
    setNodes(nds => {
      const newNodes = [...nds, newNode];
      console.log(`[DEBUG] AFTER adding node - all nodes:`, newNodes.map(n => ({ id: n.id, position: n.position })));
      return newNodes;
    });
    
    // Mark as manually positioned AFTER adding 
    setManuallyMovedNodes(prev => {
      const newSet = new Set(prev);
      newSet.add(newNode.id);
      console.log(`[DEBUG] Manually moved nodes now:`, Array.from(newSet));
      return newSet;
    });
    
    // If this is a temporary node, store it for later repositioning
    if (isTemporary) {
      // Store the node info for repositioning when the decision node is created
      const tempNodeInfo = {
        nodeId: newNode.id,
        pathType,
        identifier: pathIdentifier,
        tempSourceNodeId: sourceNodeId
      };
      console.log(`[DEBUG] Storing temporary decision child for later repositioning:`, tempNodeInfo);
      
      // Store in window for now (you might want to use a more proper state management)
      if (!(window as any).tempDecisionChildren) {
        (window as any).tempDecisionChildren = [];
      }
      (window as any).tempDecisionChildren.push(tempNodeInfo);
    }
    
    // Check position after a delay to see if something is overriding it
    setTimeout(() => {
      console.log(`[DEBUG] Checking if position was preserved...`);
      // We need to get fresh nodes from the state, not the stale closure
      setNodes(currentNodes => {
        const updatedNode = currentNodes.find(n => n.id === newNode.id);
        console.log(`[DEBUG] Node ${newNode.id} position after 1 second:`, updatedNode?.position);
        console.log(`[DEBUG] Expected position:`, newPosition);
        console.log(`[DEBUG] Position preserved:`, 
          updatedNode?.position.x === newPosition.x && updatedNode?.position.y === newPosition.y
        );
        return currentNodes; // Don't change anything, just read
      });
    }, 1000);
    
    // Note: We don't create any edges here. The decision path selection
    // will handle creating the proper decision edges via updateDecisionNodeEdges
    
    // Save to history
    saveToHistory("add");
    
    toast.success(`Created ${pathType} path node with identifier: ${pathIdentifier}`);
    
    // Return the identifier so it can be selected in the dropdown
    return pathIdentifier;
  }, [nodes, setIdentifiers, setNodes, setEdges, globalVariableRegistry, saveToHistory, setShouldRepositionAfterNodeAddition]);

  // Handle clearing decision variables and resetting to blank canvas
  const handleClearDecisionVariables = useCallback(() => {
    // No localStorage to clear since we don't persist data
    
    // Reset node counter
    resetNodeIdCounter();
    
    // Reset all component state to initial values
    setNodes(initialNodes);
    setEdges(initialEdges);
    setGlobalVariableRegistry({});
    setDecisionVariablesRegistry({});
    setIdentifiers([]);
    setLoadedTreeMetadata(null);
    
    // Reset tree status
    setCurrentTreeStatus('UNSAVED');
    setCurrentTreeName('Untitled');
    
    // Reset config sidebar state
    if (configState.groups && configState.groups.length > 0) {
      // Clear interjection groups if any exist
      clearGroups();
    }
    
    // console.log("All localStorage data cleared and state reset to blank canvas");
  }, [setNodes, setEdges, setGlobalVariableRegistry, setIdentifiers, configState, clearGroups]);

  // Handle opening the load modal
  const handleOpenLoadModal = useCallback(() => {
    setIsLoadModalVisible(true);
  }, []);

  // Handle closing the load modal
  const handleCloseLoadModal = useCallback(() => {
    setIsLoadModalVisible(false);
  }, []);

  // Handle loading a tree from the modal
  const handleLoadTree = useCallback((tree: any) => {
    if (!apiKey) {
      toast.error("API key not available");
      return;
    }

    // Silent reset before loading new tree to prevent data carryover
    setNodes(initialNodes);
    setEdges(initialEdges);
    setGlobalVariableRegistry({});
    setDecisionVariablesRegistry({});
    setIdentifiers([]);
    setIsFirstNode(true);
    setSystemPrompt("");
    clearGroups();
    resetNodeIdCounter();

    // Store the tree metadata
    setLoadedTreeMetadata({
      id: tree.id,
      conversation_flow_name: tree.conversation_flow_name,
      application_name: tree.application_name,
      intent_name: tree.intent_name,
      conversation_flow_version: tree.conversation_flow_version,
      is_latest: tree.is_latest,
      status: tree.status,
    });

    // Update tree status and name
    setCurrentTreeName(tree.conversation_flow_name);
    setCurrentTreeStatus(tree.status || 'SAVED'); // Default to SAVED if no status provided

    // Fetch the full tree configuration from backend
    const response = ConversationBuilderService.getConversationTreeConfig(tree.id, apiKey);
    
    response.subscribe({
      next: (treeConfig: any) => {
        try {
          // Extract the conversation_response from the wrapped backend response
          const backendConfig = treeConfig.conversation_response || treeConfig;
          
          // console.log("[DEBUG] Loading tree - backend response:", treeConfig);
          
          // Convert backend tree config to FlowData format
          const flowData = convertBackendConfigToFlowData(backendConfig);
          
          // Use the same import process as handleImportConfirm
          saveToHistory("add");
          const importedData = importFlowData(flowData);
          importInProgressRef.current = true; // Set flag
          
          // Update all editor state with imported data
          setNodes(importedData.nodes);
          setEdges(importedData.edges);
          setGlobalVariableRegistry(importedData.variables);
          setDecisionVariablesRegistry(importedData.decisionVariables);
          setIdentifiers(importedData.identifiers);
          setIsFirstNode(importedData.isFirstNode);
          setSystemPrompt(importedData.systemPrompt || ""); // Set system prompt from loaded tree
          
          // Load interjection groups
          if (importedData.interjectionGroups) {
            setGroups(importedData.interjectionGroups);
          }
          
          toast.success(`Loaded tree: ${tree.conversation_flow_name}`);
          setIsLoadModalVisible(false); // Close the modal
        } catch (error) {
          toast.error("Failed to load tree data");
        }
      },
      error: (error: any) => {
        toast.error("Failed to fetch tree configuration");
      }
    });
  }, [apiKey, saveToHistory, setNodes, setEdges, setGlobalVariableRegistry, setDecisionVariablesRegistry, setIdentifiers, setIsFirstNode, setGroups]);

  // Track loaded tree ID to prevent re-loading the same tree
  const loadedTreeIdRef = useRef<string | null>(null);

  // Load tree from URL parameter on component mount
  useEffect(() => {
    const treeId = searchParams.get('id');
    if (treeId && apiKey && companyId && loadedTreeIdRef.current !== treeId) {
      // Mark this tree as being loaded to prevent duplicate calls
      loadedTreeIdRef.current = treeId;
      
      // Clear any auto-saved data to prevent conflicts
      clearAutoSavedData();
      
      // First, get the tree metadata by ID
      ConversationBuilderService.listLatestConversationTreesByCompany(companyId, apiKey).subscribe({
        next: (trees: any[]) => {
          const targetTree = trees.find(tree => tree.id.toString() === treeId);
          if (targetTree) {
            // Use the existing handleLoadTree logic
            handleLoadTree(targetTree);
          } else {
            toast.error(`Tree with ID ${treeId} not found`);
          }
        },
        error: (error: any) => {
          console.error("Error loading tree from URL:", error);
          toast.error("Failed to load tree from URL");
          // Reset the ref on error so user can retry
          loadedTreeIdRef.current = null;
        }
      });
    }
    
    // Reset the ref when there's no tree ID (going to blank canvas)
    if (!treeId) {
      loadedTreeIdRef.current = null;
    }
  }, [searchParams, apiKey, companyId]);

  // Smart save flow handler - saves to backend
  const handleSaveFlow = useCallback(() => {
    // Validate flow before saving
    const flowValidation = validateFlow(nodes, globalVariableRegistry);
    if (!flowValidation.isValid) {
      setValidationErrors(flowValidation.allIssues);
      setIsValidationErrorModalVisible(true);
      return;
    }

    // NOTE: No longer saving to localStorage - only backend saves
    // try {
    //   saveFlow();
    // } catch (error) {
    //   console.error("Failed to save flow locally:", error);
    //   console.error("Failed to save flow locally");
    //   return;
    // }

    // Handle backend save with smart logic
    if (loadedTreeMetadata) {
      // Tree was loaded - get next available version from backend to avoid conflicts
          if (!companyId || !apiKey) {
      toast.error("Company ID or API key not available. Please make sure you're logged in.");
      return;
    }
      
      // Get next available version from backend
      const nextVersionRequest = ConversationBuilderService.getNextVersionNumber(
        companyId, 
        loadedTreeMetadata.application_name,
        loadedTreeMetadata.intent_name,
        loadedTreeMetadata.conversation_flow_name, 
        apiKey
      );
      
      nextVersionRequest.subscribe({
        next: (response: any) => {
          const nextVersion = response.next_version;
          
          const backendMetadata = {
            conversation_flow_name: loadedTreeMetadata.conversation_flow_name,
            company_id: String(companyId),
            application_ref_name: loadedTreeMetadata.application_name,
            action_type: loadedTreeMetadata.intent_name,
            conversation_flow_version: nextVersion.toString(),
            is_latest: true
          };
          
          handleSaveToBackend(backendMetadata);
        },
        error: (error: any) => {
          toast.error("Failed to save flow to backend");
        }
      });
    } else {
      // Blank canvas - open SaveTreeModal to get save details
      setIsSaveTreeModalVisible(true);
    }
  }, [saveFlow, loadedTreeMetadata, handleSaveToBackend, companyId, apiKey, nodes, globalVariableRegistry]);

  // Handle SaveTreeModal close
  const handleSaveTreeModalCancel = useCallback(() => {
    setIsSaveTreeModalVisible(false);
  }, []);

  // Handle SaveTreeModal save
  const handleSaveTreeModalSave = useCallback((metadata: any) => {
    handleSaveToBackend(metadata);
    setIsSaveTreeModalVisible(false);
  }, [handleSaveToBackend]);

  // Handle trigger finetune - save first, then start finetune
  const handleTriggerFinetune = useCallback(async () => {
    if (!loadedTreeMetadata || !companyId || !apiKey) {
      toast.error("Missing required data for finetune. Please ensure tree is loaded and you're logged in.");
      return;
    }

    try {
      // First, validate and save the current tree
      const flowValidation = validateFlow(nodes, globalVariableRegistry);
      if (!flowValidation.isValid) {
        setValidationErrors(flowValidation.allIssues);
        setIsValidationErrorModalVisible(true);
        return;
      }

      toast.info("Saving tree and starting finetune...");

      // Get next available version from backend to avoid conflicts
      const nextVersionRequest = ConversationBuilderService.getNextVersionNumber(
        companyId, 
        loadedTreeMetadata.application_name,
        loadedTreeMetadata.intent_name,
        loadedTreeMetadata.conversation_flow_name, 
        apiKey
      );
      
      const nextVersionResponse = await new Promise((resolve, reject) => {
        nextVersionRequest.subscribe({
          next: resolve,
          error: reject
        });
      });

      const nextVersion = (nextVersionResponse as any).next_version;
      
      const backendMetadata = {
        conversation_flow_name: loadedTreeMetadata.conversation_flow_name,
        company_id: String(companyId),
        application_ref_name: loadedTreeMetadata.application_name,
        action_type: loadedTreeMetadata.intent_name,
        conversation_flow_version: nextVersion.toString(),
        is_latest: true
      };

      // Save the tree first
      const flowData = exportFlowData(
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        configState.groups,
        systemPrompt,
        {
          title: "Conversation Flow Finetune Save",
          description: `Saved for finetune on ${new Date().toLocaleString()}`,
          status: "draft"
        }
      );

      const backendRequest = convertFlowDataToConversationBuilderRequest(flowData, backendMetadata);
      
      const saveRequest = ConversationBuilderService.saveAndGetPaths(
        backendRequest,
        () => {}, // Loading handler
        apiKey
      );

      const saveResponse = await new Promise((resolve, reject) => {
        saveRequest.subscribe({
          next: resolve,
          error: reject
        });
      });

      const conversationBuilderConfigId = (saveResponse as any).conversation_builder_config_id;

                toast.info("Tree saved! Starting finetune process...");

      // Now trigger the finetune using the service
      const finetuneRequest = ConversationBuilderService.createConversationTreeFinetune(
        companyId,
        loadedTreeMetadata.application_name,
        loadedTreeMetadata.intent_name,
        loadedTreeMetadata.conversation_flow_name,
        conversationBuilderConfigId,
        apiKey
      );

      const finetuneResult = await new Promise((resolve, reject) => {
        finetuneRequest.subscribe({
          next: resolve,
          error: reject
        });
      }) as any;
      
      toast.success(`Finetune started successfully! Composition: ${finetuneResult.composition?.id}, Test Set: ${finetuneResult.test_set?.id}, Finetune Run: ${finetuneResult.finetune_run?.id}`);

      // Update loaded tree metadata with new version
      setLoadedTreeMetadata(prev => prev ? {
        ...prev,
        conversation_flow_version: nextVersion,
        is_latest: true
      } : null);

    } catch (error) {
      toast.error(`Failed to trigger finetune: ${error}`);
    }
  }, [nodes, globalVariableRegistry, loadedTreeMetadata, companyId, apiKey, edges, decisionVariablesRegistry, identifiers, configState.groups, systemPrompt]);

  // Validation modal state
  const [isValidationErrorModalVisible, setIsValidationErrorModalVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // **SIMPLE: Load cached tree on mount**
  useEffect(() => {
    const cached = localStorage.getItem("maitai-conversation-tree-cache");
    if (cached) {
      try {
        const data = JSON.parse(cached);
        console.log("[Debug] 🔄 Loading cached tree");
        setNodes(data.nodes || []);
        setEdges(data.edges || []);
        setGlobalVariableRegistry(data.globalVariableRegistry || {});
        setDecisionVariablesRegistry(data.decisionVariablesRegistry || {});
        setIdentifiers(data.identifiers || []);
      } catch (error) {
        console.error("Failed to load cached tree:", error);
      }
    } else {
      console.log("[Debug] 📭 No cached tree found");
    }
  }, []);

  // **OPTIMIZED: Save to cache only when counts change with debouncing for large trees**
  useEffect(() => {
    const debounceTime = nodes.length > 50 ? 3000 : 1000; // Longer debounce for large trees
    const timer = setTimeout(() => {
      const cache = {
        nodes,
        edges,
        globalVariableRegistry,
        decisionVariablesRegistry,
        identifiers,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem("maitai-conversation-tree-cache", JSON.stringify(cache));
      console.log("[Debug] 💾 Tree saved to cache");
    }, debounceTime);
    
    return () => clearTimeout(timer);
  }, [nodes.length, edges.length, Object.keys(globalVariableRegistry).length, identifiers.length]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }} className="z-0">
      {/* Toolbar component replaces individual buttons */}
      <Toolbar
        onResetConversation={handleResetConversation}
        onReorganize={customReorganizeTree}
        onUndo={undoLastAction}
        onNewTree={handleNewTree}
        onSave={handleSaveFlow}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSavedTimestamp={lastSavedTimestamp}
        actionHistory={actionHistory}
        autoSaveStatus={autoSaveStatus}
      />
      
      {/* Context Menu */}
      <ContextMenu
        contextMenu={contextMenu}
        clipboard={clipboard}
        onCopyNode={copyNode}
        onCopyBranch={copyBranch}
        onPasteNode={pasteNode}
        onPasteBranch={pasteBranch}
        onDeleteNode={showDeleteNodeConfirmation}
        onDeleteBranch={showDeleteBranchConfirmation}
        onHide={hideContextMenu}
      />

      {/* Canvas Context Menu */}
      <CanvasContextMenu
        canvasContextMenu={canvasContextMenu}
        onAddNode={handleAddNodeAtPosition}
        onHide={hideCanvasContextMenu}
      />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodesDelete={onNodesDelete}
        onEdgesDelete={onEdgesDelete}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={onInit}
        fitView
        fitViewOptions={{ padding: 0.25 }}
        proOptions={{ hideAttribution: true }}
        maxZoom={1}
        minZoom={0.1}
      >
        <Controls>
          <ControlButton
            onClick={toggleMiniMap}
            title={isMiniMapVisible ? "Hide Mini-map" : "Show Mini-map"}
            className="bg-card border-border text-foreground hover:bg-accent"
            style={{
              color: isMiniMapVisible ? "#21b892" : undefined
            }}
          >
            <BorderOuterOutlined />
          </ControlButton>
        </Controls>
        {isMiniMapVisible && (
          <MiniMap 
            zoomable 
            pannable 
          />
        )}
        <Background gap={12} size={1} />
      </ReactFlow>

      {/* Import Flow Modal */}
      <ImportFlowModal
        visible={isImportModalVisible}
        onCancel={handleImportCancel}
        onImport={handleImportConfirm}
      />

      {/* All Modals */}
      <Modals
        modalState={modalState}
        placeholderModalState={placeholderModalState}
        deleteModalState={deleteModalState}
        previewModalState={previewModalState}
        standaloneModalState={standaloneModalState}
        isResetModalVisible={isResetModalVisible}
        isDeleteInterjectionGroupModalVisible={isDeleteInterjectionGroupModalVisible}
        interjectionGroupToDelete={interjectionGroupToDelete}
        externalToolCallModal={externalToolCallModal}
        onExternalToolCallEdit={handleExternalToolCallEdit}
        onExternalToolCallCancel={handleExternalToolCallCancel}
        isFirstNode={isFirstNode}
        identifiers={identifiers}
        searchText={searchText}
        globalVariableRegistry={globalVariableRegistry}
        decisionVariablesRegistry={decisionVariablesRegistry}
        edges={edges}
        nodes={nodes}
        setModalState={setModalState}
        setPlaceholderModalState={setPlaceholderModalState}
        setDeleteModalState={setDeleteModalState}
        setPreviewModalState={setPreviewModalState}
        setStandaloneModalState={setStandaloneModalState}
        setEdges={setEdges}
        setSearchText={setSearchText}
        onOk={nodeHandleOk}
        onCancel={nodeHandleCancel}
        onPlaceholderModalCancel={handlePlaceholderModalCancel}
        onDeleteModalCancel={nodeHandleDeleteModalCancel}
        onDeleteNodeConfirm={nodeHandleDeleteNodeConfirm}
        onDeleteBranchConfirm={nodeHandleDeleteBranchConfirm}
        onResetConfirm={handleResetConfirm}
        onResetCancel={handleResetCancel}
        onStandaloneOk={handleCreateStandaloneNode}
        onStandaloneCancel={handleStandaloneCancel}
        onPreviewModalClose={handlePreviewModalClose}
        onPreviewPlaceholderClick={handlePreviewPlaceholderClick}
        onDeleteInterjectionGroupConfirm={handleDeleteInterjectionGroupConfirm}
        onDeleteInterjectionGroupCancel={handleDeleteInterjectionGroupCancel}
        onCreateDecisionVariable={handleCreateDecisionVariable}
        onCreateNodeForDecisionPath={handleCreateNodeForDecisionPath}
        addIdentifier={addIdentifier}
        hasChildWithIdentifier={hasChildWithIdentifier}
        getDisabledIdentifiers={getDisabledIdentifiers}
        getAvailableIdentifiers={getAvailableIdentifiers}
        isIdentifierInUse={isIdentifierInUse}
        getCurrentExamples={getCurrentExamples}
        addExampleToPlaceholder={addExampleToPlaceholder}
        removeExampleFromPlaceholder={removeExampleFromPlaceholder}
      />

      {/* Config Sidebar */}
      <ConfigSidebar
        configState={configState}
        // New group-based handlers
        onStartAddingNewGroup={startAddingNewGroup}
        onStartEditingGroup={startEditingGroup}
        onCancelEditing={cancelEditing}
        onSaveInterjectionGroup={saveInterjectionGroup}
        onDeleteInterjectionGroup={showDeleteInterjectionGroupConfirmation}
        onStartAddingContent={startAddingContent}
        onStartEditingContent={startEditingContent}
        onSaveContent={saveContent}
        onSaveContentForGroup={saveContentForGroup}
        onDeleteContent={deleteContent}
        // Export/Import/Load handlers
        onExportFlow={handleExportFlow}
        onImportFlow={handleImportConfirm}
        onLoad={handleOpenLoadModal}
        onLoadTree={handleLoadTree}
        onClearDecisionVariables={handleClearDecisionVariables}
        onTriggerFinetune={handleTriggerFinetune}
        // Placeholder management handlers
        onUpdateGlobalVariableRegistry={setGlobalVariableRegistry}
        onEditNode={handleEditNodeFromPlaceholders}
        onNodeFocus={handleNodeFocus}
        onEditToolCall={handleEditToolCallFromSidebar}
        // Decision variables handlers
        decisionVariablesRegistry={decisionVariablesRegistry}
        onUpdateDecisionVariablesRegistry={setDecisionVariablesRegistry}
        onUpdateNodes={setNodes}
        // Tree status handlers
        currentTreeStatus={currentTreeStatus}
        currentTreeName={currentTreeName}
        onTreeStatusChange={setCurrentTreeStatus}
        // System prompt handlers
        systemPrompt={systemPrompt}
        onSystemPromptChange={setSystemPrompt}
        // Flow data for export/import
        nodes={nodes}
        edges={edges}
        globalVariableRegistry={globalVariableRegistry}
        identifiers={identifiers}
        interjectionGroups={configState.groups}
        loadedTreeMetadata={loadedTreeMetadata}
      />

      {/* Load Tree Modal */}
      <LoadTreeModal
        visible={isLoadModalVisible}
        onCancel={handleCloseLoadModal}
        onLoadTree={handleLoadTree}
      />

      {/* Save Tree Modal */}
      <SaveTreeModal
        visible={isSaveTreeModalVisible}
        onCancel={handleSaveTreeModalCancel}
        onSave={handleSaveTreeModalSave}
      />

      {/* New Tree Confirmation Modal */}
      <NewTreeConfirmationModal
        isOpen={isNewTreeModalVisible}
        onConfirm={handleNewTreeConfirm}
        onCancel={handleNewTreeCancel}
      />

      {/* Validation Error Modal */}
      <ValidationErrorModal
        visible={isValidationErrorModalVisible}
        onClose={() => setIsValidationErrorModalVisible(false)}
        validationErrors={validationErrors}
      />
    </div>
  );
};

export default ConversationTree; 